// promptGenerators.js
import countryFoodPreferences from './country_food_preferences.json';
import inspirationsData from './inspirations.json';
import { getCountryCurrency, isCountrySupported, DEFAULT_COUNTRY } from '../services/countryService';
import { getUserCountry } from '../services/apiService';
import { usesMetricSystem } from './measurementSystemUtils';
import { filterByRestrictions, shouldExcludeDairy, getForbiddenIngredientsText } from './dietaryFilters';
import { getFullNamingExamplesBlock, getProductHintRule, PRODUCT_FORM_VALUES } from './ingredientNamingRules';
import { getSupermarketReadyRule, getIngredientCompletenessRule, getNoBeveragesRule, getProportionalityRuleFull, getIngredientQualitySelfCheck, getCookingComplexityRuleFull, getFreshnessDistributionRule } from './ingredientQualityRules';
import { COOKING_TIME_LIMITS, getPassiveCookingBonus } from './cookingTimeLimits';
import dietAndFoodPreferences from './dietandfoodpreferences.json';

// Lookup map: key → full dietary option object (to restore missing fields like `type` after backend serialization)
const DIETARY_OPTIONS_BY_KEY = Object.fromEntries(
  (dietAndFoodPreferences.dietaryOptions || []).map(opt => [opt.key, opt])
);

const STRICT_DIET_STYLE_RESTRICTION_KEYS = new Set(['vegetarian', 'vegan']);

const inferRestrictionKeysFromDietaryText = (dietaryPreferences) => {
  const value = String(dietaryPreferences || '').toLowerCase();
  const keys = [];

  if (value.includes('vegetarian')) keys.push('vegetarian');
  if (value.includes('vegan')) keys.push('vegan');
  if (value.includes('halal')) keys.push('halal');
  if (value.includes('pork')) keys.push('noPork');
  if (value.includes('dairy') || value.includes('lactose')) keys.push('noDairy');
  if (value.includes('egg')) keys.push('noEggs');
  if (value.includes('shellfish') || value.includes('shrimp') || value.includes('prawn')) keys.push('noShellfish');
  if (value.includes('gluten')) keys.push('glutenFree');
  if (value.includes('nut') || value.includes('peanut')) keys.push('noNuts');
  if (value.includes('mushroom')) keys.push('noMushrooms');
  if ((/\bfish\b/.test(value) || value.includes('seafood')) && !value.includes('shellfish')) keys.push('noFish');

  return Array.from(new Set(keys));
};

// Re-export PromptBuilder for unified access
export { PromptBuilder } from './PromptBuilder';

// Helper function to get current month name for seasonality context
const getCurrentMonthName = () => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return months[new Date().getMonth()];
};

/**
 * Determines the budget goal tier based on user budget and country pricing.
 * 
 * @param {Object|number} userBudget - Budget object with min property or raw number
 * @param {string} country - Country code (e.g., "Spain", "USA")
 * @param {number} numberOfPeople - Number of people to calculate per-person budget
 * @returns {string} Budget goal tier: "Survival", "Budget", "Standard", "Gourmet", or "Luxury"
 */
const determineBudgetGoal = (userBudget, country, numberOfPeople) => {
  // If userBudget is an object, use min for calculations
  const budgetValue = typeof userBudget === 'object' && userBudget !== null ? userBudget.min : userBudget;
  const countryData = countryFoodPreferences[country];
  
  // Fallback to USA if country data not available
  if (!countryData) {
    console.warn(`No food preferences data for country: ${country}, falling back to default`);
    return determineBudgetGoal(budgetValue, 'USA', numberOfPeople);
  }
  
  const budgetPerPerson = budgetValue / numberOfPeople;

  const goals = ['survival', 'budget', 'standard', 'gourmet', 'luxury'];

  for (let goal of goals) {
    const [min, max] = countryData.budget[goal];
    if ((max === null && budgetPerPerson >= min) || (budgetPerPerson >= min && budgetPerPerson <= max)) {
      return goal.charAt(0).toUpperCase() + goal.slice(1); // Capitalized
    }
  }
  return 'Standard'; // Fallback
};

/**
 * Generates budget goal explanation with country-specific ingredient hints.
 * Filters ingredient hints based on dietary restrictions to avoid conflicts.
 * Merges cuisine kit anchors when a specific cuisine is selected.
 * 
 * @param {string} budgetGoal - Budget tier: "Survival", "Budget", "Standard", "Gourmet", or "Luxury"
 * @param {Object} countryData - Country food preferences data
 * @param {string[]} restrictionKeys - Array of dietary restriction keys (e.g., ["noDairy", "noNuts", "vegetarian"])
 * @param {string} selectedCuisine - Selected cuisine (e.g., "Russian") or "Surprise Me"
 * @returns {Object} Object with prompt, ingredientHints, portions, minBudget, cuisineAnchors, and cuisineAvoid properties
 */
const getBudgetGoalExplanation = (budgetGoal, countryData, restrictionKeys = [], selectedCuisine = 'Surprise Me') => {
  // Map budget goals to tier keys in countryData.budgetTierIngredients
  const tierKeyMap = {
    Survival: 'budget', // Survival uses budget tier hints
    Budget: 'budget',
    Standard: 'standard',
    Gourmet: 'gourmet',
    Luxury: 'gourmet' // Luxury uses gourmet tier hints (highest available)
  };

  const tierKey = tierKeyMap[budgetGoal] || 'standard';
  const countryTier = countryData?.budgetTierIngredients?.[tierKey];
  
  // Check if dairy should be excluded entirely
  const excludeDairy = shouldExcludeDairy(restrictionKeys);

  // Helper: Apply cuisine kit filtering and reordering to a tier list
  // Removes items in cuisineAvoid, boosts items in cuisineAnchors to front
  const applyCuisineKitToTier = (arr, cuisineAnchorsArr = [], cuisineAvoidArr = []) => {
    if (!arr?.length) return [];
    
    const avoid = new Set((cuisineAvoidArr || []).map(s => s.toLowerCase()));
    const anchors = new Set((cuisineAnchorsArr || []).map(s => s.toLowerCase()));

    // 1) Remove items that are in the avoid list
    const filtered = arr.filter(x => !avoid.has(String(x).toLowerCase()));

    // 2) Stable sort: anchors first, then rest
    const anchored = [];
    const rest = [];
    for (const x of filtered) {
      (anchors.has(String(x).toLowerCase()) ? anchored : rest).push(x);
    }
    return [...anchored, ...rest];
  };

  // Get cuisine kit for single-cuisine selection (for tier filtering)
  let activeCuisineKit = null;
  if (selectedCuisine && selectedCuisine !== 'Surprise Me' && countryData?.cuisineKits) {
    const cuisines = selectedCuisine.split(',').map(c => c.trim()).filter(Boolean);
    if (cuisines.length === 1) {
      activeCuisineKit = countryData.cuisineKits[cuisines[0]] || null;
    }
  }

  // Build country-specific ingredient hints, filtering by dietary restrictions
  // When a single cuisine is selected, also filter out avoid items and boost anchors
  const buildIngredientHints = (tier, fallbackHints) => {
    if (!tier) {
      // Filter fallback hints by dietary restrictions (split by semicolons, filter segments, rejoin)
      if (restrictionKeys?.length && fallbackHints) {
        const segments = fallbackHints.split(';').map(s => s.trim()).filter(Boolean);
        const filtered = filterByRestrictions(segments, restrictionKeys);
        return filtered.join('; ') || fallbackHints;
      }
      return fallbackHints;
    }
    
    // Helper: Fisher-Yates shuffle to get random items from array
    const getRandomItems = (arr, count) => {
      if (!arr || arr.length === 0) return [];
      if (arr.length <= count) return [...arr];
      
      // Fisher-Yates shuffle on a copy
      const shuffled = [...arr];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled.slice(0, count);
    };
    
    // Filter each category by dietary restrictions first
    let filteredProteins = filterByRestrictions(tier.proteins, restrictionKeys);
    let filteredProduce = filterByRestrictions(tier.produce, restrictionKeys);
    let filteredDairy = excludeDairy ? [] : filterByRestrictions(tier.dairy, restrictionKeys);
    let filteredPantry = filterByRestrictions(tier.pantry, restrictionKeys);
    
    // If a single cuisine kit is active, apply cuisine filtering/reordering
    if (activeCuisineKit) {
      const anchors = activeCuisineKit.anchors || {};
      const avoid = activeCuisineKit.avoid || [];
      
      filteredProteins = applyCuisineKitToTier(filteredProteins, anchors.proteins, avoid);
      filteredProduce = applyCuisineKitToTier(filteredProduce, anchors.produce, avoid);
      filteredDairy = applyCuisineKitToTier(filteredDairy, anchors.dairy, avoid);
      filteredPantry = applyCuisineKitToTier(filteredPantry, anchors.pantry, avoid);
    }
    
    const parts = [];
    // Randomly select items from each category for variety
    if (filteredProteins?.length) parts.push(`Proteins: ${getRandomItems(filteredProteins, 7).join(', ')}`);
    if (filteredProduce?.length) parts.push(`Produce: ${getRandomItems(filteredProduce, 7).join(', ')}`);
    if (filteredDairy?.length) parts.push(`Dairy: ${getRandomItems(filteredDairy, 7).join(', ')}`);
    if (filteredPantry?.length) parts.push(`Pantry: ${getRandomItems(filteredPantry, 7).join(', ')}`);
    if (tier.avoid?.length) parts.push(`AVOID: ${tier.avoid.join(', ')}`);
    
    return parts.length ? parts.join('. ') + '.' : fallbackHints;
  };

  const explanations = {
    Survival: { 
      prompt: `Lowest-cost nutritious meals for everyday home cooking. CRITICAL: minimize waste and stay strictly within budget range.`,
      fallbackHints: `Use economical proteins (eggs, canned legumes, frozen chicken thighs); filling staples (rice, pasta, potatoes); frozen/canned vegetables; store-brand basics. AVOID: premium cuts, fresh seafood, specialty items, organic products.`,
      portions: 'minimal portions; stretch proteins with beans/grains/potatoes',
      minBudget: 0 
    },
    Budget: { 
      prompt: `Affordable, varied meals using common supermarket ingredients and simple home cooking. Ensure totalCost stays within budget range.`,
      fallbackHints: `Prefer economical proteins (chicken thighs, eggs, canned tuna, legumes); seasonal vegetables; store-brand or value products. AVOID: premium cuts, fresh seafood, imported cheese, organic products, specialty items.`,
      portions: 'budget serving sizes; stretch proteins with beans/grains',
      minBudget: 0 
    }, 
    Standard: { 
      prompt: `Balanced everyday home-cooked meals with common ingredients; avoid hard-to-find items; minimize repetition; reuse leftovers. Stay within or slightly under budget to allow for occasional higher-quality ingredients.`,
      fallbackHints: `Use a mix of proteins (chicken breast, pork, eggs, fish fillets); fresh seasonal produce; standard brand products. AVOID: wagyu beef, lobster, truffle products, saffron, luxury deli items.`,
      portions: 'standard serving sizes; balanced protein-to-vegetable ratios',
      minBudget: 0 
    },
    Gourmet: { 
      prompt: `Diverse, flavorful home-cooked meals with higher-quality ingredients where they matter.`,
      fallbackHints: `Choose premium proteins (salmon fillets, sea bass, prawns, beef sirloin, duck breast); quality cheeses (aged Parmesan, Gruyère); fresh herbs; premium olive oil; specialty items (sundried tomatoes, capers, olives). AVOID: canned vegetables, frozen fish sticks, processed cheese, margarine, store-brand basics.`,
      portions: 'quality over quantity',
      minBudget: 0.5 
    },
    Luxury: { 
      prompt: `Premium, diverse home-cooked meals with high-quality ingredients. CRITICAL: totalCost must reach the budget range.`,
      fallbackHints: `Use luxury proteins (lobster, crab, wagyu beef, lamb rack, whole sea bream); premium ingredients (saffron, truffle oil, aged balsamic, caviar); high-quality cheeses. AVOID: any budget or standard-tier products; always choose premium options.`,
      portions: 'generous luxury portions; use premium ingredients liberally',
      minBudget: 0.3 
    }
  };

  const baseExplanation = explanations[budgetGoal] || explanations['Standard'];
  
  // Build the final ingredient hints from country data or fallback
  const ingredientHints = buildIngredientHints(countryTier, baseExplanation.fallbackHints);
  
  // Use country-specific portions if available, otherwise fall back to base explanation
  const portions = countryTier?.portions || baseExplanation.portions;
  
  // Extract cuisine kit if a specific cuisine is selected (not "Surprise Me")
  let cuisineAnchors = null;
  let cuisineAvoid = [];
  let flavorAnchors = [];
  
  if (selectedCuisine && selectedCuisine !== 'Surprise Me' && countryData?.cuisineKits) {
    // Handle single cuisine or comma-separated cuisines
    const cuisines = selectedCuisine.split(',').map(c => c.trim()).filter(Boolean);
    
    // If only one cuisine selected, apply its kit strongly
    if (cuisines.length === 1) {
      const kit = countryData.cuisineKits[cuisines[0]];
      if (kit) {
        cuisineAnchors = kit.anchors;
        cuisineAvoid = filterByRestrictions(kit.avoid || [], restrictionKeys);
        flavorAnchors = filterByRestrictions(kit.flavorAnchors || [], restrictionKeys);
      }
    } else if (cuisines.length > 1) {
      // For multiple cuisines, merge anchors but be more permissive with avoid
      const mergedAnchors = { proteins: [], produce: [], dairy: [], pantry: [] };
      const mergedFlavors = [];
      
      for (const cuisineName of cuisines) {
        const kit = countryData.cuisineKits[cuisineName];
        if (kit?.anchors) {
          for (const cat of ['proteins', 'produce', 'dairy', 'pantry']) {
            if (kit.anchors[cat]) {
              mergedAnchors[cat].push(...kit.anchors[cat]);
            }
          }
        }
        if (kit?.flavorAnchors) {
          mergedFlavors.push(...kit.flavorAnchors);
        }
      }
      
      // Dedupe
      for (const cat of Object.keys(mergedAnchors)) {
        mergedAnchors[cat] = [...new Set(mergedAnchors[cat])];
      }
      
      cuisineAnchors = mergedAnchors;
      flavorAnchors = filterByRestrictions([...new Set(mergedFlavors)], restrictionKeys);
      // Don't apply avoid list when mixing cuisines (too restrictive)
      cuisineAvoid = [];
    }
  }
  
  return {
    prompt: baseExplanation.prompt,
    ingredientHints: ingredientHints,
    portions: portions,
    minBudget: baseExplanation.minBudget,
    cuisineAnchors: cuisineAnchors,
    cuisineAvoid: cuisineAvoid,
    flavorAnchors: flavorAnchors
  };
};

const defineSelectedCuisines = (selectedCuisine, countryData, totalMeals = 9) => {
  if (selectedCuisine !== "Surprise Me") {
    // User selected specific cuisine(s)
    return { cuisineDistribution: `all meals from ${selectedCuisine} cuisine(s)` };
  }
  else {
    // Handle both string arrays and object arrays with {v, w} format
    // Convert percentages to exact meal counts for clarity
    const rawPreferredCuisines = countryData.preferredCuisines || [];
    
    // Normalize to object format with weights
    const cuisinesWithWeights = rawPreferredCuisines.map(item => {
      if (typeof item === 'string') {
        const match = item.match(/^(.+?)\s*\((\d+)%\)$/);
        if (match) {
          return { name: match[1].trim(), weight: parseInt(match[2], 10) };
        }
        return { name: item.trim(), weight: Math.round(100 / rawPreferredCuisines.length) };
      } else if (typeof item === 'object' && item.v && item.w) {
        return { name: item.v, weight: item.w };
      }
      return null;
    }).filter(Boolean);
    
    if (cuisinesWithWeights.length === 0) {
      return { cuisineDistribution: 'varied cuisines' };
    }
    
    // Convert percentages to exact meal counts
    const totalWeight = cuisinesWithWeights.reduce((sum, c) => sum + c.weight, 0);
    const cuisineAllocations = cuisinesWithWeights.map(c => ({
      name: c.name,
      exactCount: (c.weight / totalWeight) * totalMeals,
      baseCount: Math.floor((c.weight / totalWeight) * totalMeals),
      remainder: ((c.weight / totalWeight) * totalMeals) % 1
    }));
    
    // Distribute remaining meals by largest remainder
    let remaining = totalMeals - cuisineAllocations.reduce((sum, c) => sum + c.baseCount, 0);
    const sortedByRemainder = [...cuisineAllocations].sort((a, b) => b.remainder - a.remainder);
    
    for (let i = 0; i < remaining && i < sortedByRemainder.length; i++) {
      sortedByRemainder[i].baseCount += 1;
    }
    
    // Build the distribution string with exact counts
    const distributionParts = cuisineAllocations
      .filter(c => c.baseCount > 0)
      .map(c => `${c.name}: ${c.baseCount} meal${c.baseCount > 1 ? 's' : ''}`)
      .join(', ');
    
    return { cuisineDistribution: distributionParts };
  }
};

// Function to generate randomization seed for variety
const generateVarietySeed = () => {
  const randomElements = [
    Math.floor(Math.random() * 10000),
    new Date().getTime() % 100000,
    Math.floor(Math.random() * 1000),
    Math.floor(Math.random() * 500)
  ];
  return randomElements.join('');
};

const getInspirationTitle = (item) => (typeof item === 'string' ? item : (item?.title || ''));

const DISALLOWED_OVERNIGHT_RECIPE_PATTERN = /\b(overnight|night before|previous night|all night|soaked oats|overnight oats|overnight marinade|avena nocturna|avena notturna|de un d[ií]a para otro|d'un dia per l'altre|durante la noche|toda la noche|noche anterior|tota la nit|nit abans|toute la nuit|la veille|peste noapte|durante la notte|tutta la notte|sera prima)\b|\b(на ночь|на ніч|всю ночь|всю ніч|с вечера|з вечора|ночная овсянка|нічна вівсянка|овсянка на ночь|вівсянка на ніч)\b/i;
const SLOW_BREAKFAST_PATTERN = /\b(bizcocho|sponge cake|magdalena|cake|churro|churros|baked|bake|muffin|pastry|brioche|croissant|tortilla de patatas|potato omelette|chia\s+pudding|panna cotta|mousse|no-bake cheesecake|cheesecake)\b|\bchilled\s+(?:pudding|custard|mousse|dessert|oats?|chia)\b|\b(?:let|leave|allow|refrigerate|chill)\b.{0,50}\b(?:set|firm|thicken|overnight|hours?)\b/i;

const hasDisallowedOvernightRecipeText = (value) => DISALLOWED_OVERNIGHT_RECIPE_PATTERN.test(String(value || ''));

const removeOvernightInspirations = (items = []) =>
  items.filter(item => !hasDisallowedOvernightRecipeText(getInspirationTitle(item)));

const QUICK_BREAKFAST_FALLBACKS = [
  'Fresh fruit bowl with pumpkin seeds',
  'Rice cakes with tomato and olive oil',
  'Corn tortillas with avocado and tomato',
  'Chickpea tomato breakfast salad',
  'Pan con tomate with olive oil',
  'Tomato toast with serrano ham',
  'Plain folded omelette (tortilla francesa) with bread',
  'Plain yogurt with honey and walnuts',
  'Yogurt with seasonal fresh fruit and toasted seeds',
  'Curd cheese (requeson) with honey and nuts',
  'Toast with fresh cheese and honey'
];

const isQuickSafeBreakfastInspiration = (item) => {
  const title = getInspirationTitle(item);
  return title && !hasDisallowedOvernightRecipeText(title) && !SLOW_BREAKFAST_PATTERN.test(title);
};

const getCuisineNamesForInspirationFallback = (selectedCuisine, countryData) => {
  if (selectedCuisine && selectedCuisine !== 'Surprise Me') {
    return selectedCuisine.split(',').map(cuisine => cuisine.trim()).filter(Boolean);
  }

  return (countryData?.preferredCuisines || [])
    .map(item => {
      if (typeof item === 'string') {
        const match = item.match(/^(.+?)\s*\(\d+%\)$/);
        return (match ? match[1] : item).trim();
      }
      return item?.v?.trim();
    })
    .filter(Boolean);
};

const dedupeTitles = (items) => {
  const seen = new Set();
  const result = [];

  for (const item of items) {
    const title = getInspirationTitle(item);
    const key = title.toLowerCase();
    if (!title || seen.has(key)) continue;
    seen.add(key);
    result.push(title);
  }

  return result;
};

const applyQuickBreakfastPolicy = (breakfastInspirations, selectedCuisine, countryData, restrictionKeys = []) => {
  const quickSafeTitles = dedupeTitles((breakfastInspirations || []).filter(isQuickSafeBreakfastInspiration));
  const quickSafe = restrictionKeys.length
    ? filterByRestrictions(quickSafeTitles, restrictionKeys)
    : quickSafeTitles;
  const cuisineNames = getCuisineNamesForInspirationFallback(selectedCuisine, countryData);
  const fallbackPool = [];

  for (const cuisineName of cuisineNames) {
    const cuisineBreakfasts = inspirationsData[cuisineName]?.breakfast || [];
    fallbackPool.push(...cuisineBreakfasts.filter(isQuickSafeBreakfastInspiration));
  }

  fallbackPool.push(...QUICK_BREAKFAST_FALLBACKS);

  const allowedFallbacks = restrictionKeys.length
    ? filterByRestrictions(dedupeTitles(fallbackPool), restrictionKeys)
    : dedupeTitles(fallbackPool);

  const merged = dedupeTitles([...quickSafe, ...allowedFallbacks]);
  return merged.slice(0, 3);
};

// Function to generate variety-inducing cooking methods and meal types, using weighted country configuration when available
const getVarietyPrompts = (varietySeed, countryData) => {
  // Helper: deterministic pseudo-random indexer based on seed
  const timestamp = Date.now(); // Add timestamp for variety
  const seedNum = parseInt(String(varietySeed).slice(-5), 10);
  const base = Number.isFinite(seedNum) ? seedNum : 137;
  const randIdx = (len, mul = 1, add = 0) => (Math.abs((base + timestamp) * (mul + 31) + add) % len);

  // Pull country variety, allow multiple schemas
  const variety = countryData?.variety || {};

  // Default-country weight template (sourced from the USA dataset), used only as numeric fallback
  const defaultWeightTemplate = (() => {
    const defaultCountryVariety = countryFoodPreferences?.USA?.variety;
    if (defaultCountryVariety && Array.isArray(defaultCountryVariety.cuisineAccentsWeighted) && defaultCountryVariety.cuisineAccentsWeighted.length) {
      return defaultCountryVariety.cuisineAccentsWeighted.map(x => Number(x.w) || 1);
    }
    // Safe minimal fallback
    return [3, 2, 1, 1, 1, 1, 1, 1, 1, 1];
  })();

  // Fallback value lists
  const fallbackCookingValues = (() => {
    // Prefer using default country's cooking.core labels if available to keep language consistent
    const defaultCoreMethods = countryFoodPreferences?.USA?.variety?.cooking?.core;
    if (Array.isArray(defaultCoreMethods) && defaultCoreMethods.length) {
      return defaultCoreMethods.map(i => (typeof i === 'string' ? i : (i.v ?? i.value ?? i.name ?? i.label ?? '')).toString()).filter(Boolean);
    }
    return [
      'baked (oven)',
      'sheet-pan roasted (oven)',
      'skillet / sautéed (stovetop)',
      'stir-fried (stovetop)',
      'pan-seared (stovetop)',
      'simmered / one-pot (stovetop)',
      'braised (stovetop/oven)',
      'steamed (stovetop)',
      'boiled / poached (stovetop)',
      'no-cook / assemble'
    ];
  })();

  const fallbackMealStyles = variety.mealStylesCoverage || [
    'tacos or wraps',
    'grain/rice bowls',
    'pasta or noodle mains',
    'main-dish salads',
    'sandwiches or melts',
    'soups / chili / stews',
    'sheet-pan meals',
    'skillet meals / stir-fries',
    'casseroles or bakes'
  ];

  // Normalize helpers
  const toValue = (item) => {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    return (item.v ?? item.value ?? item.name ?? item.label ?? item.type ?? '').toString();
  };
  const toWeight = (item, def = 1) => {
    if (item && typeof item === 'object') {
      const w = Number(item.w ?? item.weight);
      if (Number.isFinite(w) && w > 0) return w;
    }
    return def;
  };
  const dedupePreserveOrder = (arr) => {
    const seen = new Set();
    const out = [];
    for (const x of arr) { if (!seen.has(x) && x) { seen.add(x); out.push(x); } }
    return out;
  };

  // Build weighted items list for cooking methods
  const buildCookingWeighted = () => {
    const cookingSrc = (variety.cooking && Array.isArray(variety.cooking.core) && variety.cooking.core.length)
      ? variety.cooking.core
      : (Array.isArray(variety.cookingMethods) && variety.cookingMethods.length ? variety.cookingMethods : null);

    if (cookingSrc) {
      // Use weights from country variety when provided; default 1 otherwise
      const values = cookingSrc.map(i => ({ value: toValue(i), w: toWeight(i, 1) })).filter(i => i.value);
      // Deduplicate merging weights
      const acc = new Map();
      for (const { value, w } of values) {
        acc.set(value, (acc.get(value) || 0) + (Number.isFinite(w) ? w : 1));
      }
      return Array.from(acc.entries()).map(([value, w]) => ({ value, w }));
    }

    // Fallback: map default-country numeric template onto fallbackCookingValues by index
    const items = fallbackCookingValues.map((value, idx) => ({ value, w: defaultWeightTemplate[idx % defaultWeightTemplate.length] || 1 }));
    return items;
  };

  const cookingWeighted = buildCookingWeighted();

  // Weighted sampling without replacement (replicate by weight)
  const sampleWeightedDistinct = (items, k) => {
    if (!Array.isArray(items) || !items.length) return [];
    // Build a bounded pool replicating by weight
    const pool = [];
    for (const it of items) {
      const reps = Math.max(1, Math.min(20, Math.floor(it.w || 1))); // cap to keep pool reasonable
      for (let i = 0; i < reps; i++) pool.push(it.value);
    }
    const unique = dedupePreserveOrder(pool);
    const picked = [];
    let mul = 1;
    while (picked.length < Math.min(k, unique.length)) {
      const idx = randIdx(unique.length, mul, picked.length);
      const cand = unique[idx];
      if (!picked.includes(cand)) picked.push(cand);
      mul += 7;
      if (mul > 1000) break;
    }
    // If we still need more (rare), fill sequentially
    for (const v of unique) { if (picked.length >= k) break; if (!picked.includes(v)) picked.push(v); }
    return picked.slice(0, k);
  };

  // Select 4 cooking methods for variety
  const methodsSelected = sampleWeightedDistinct(cookingWeighted, 4);

  // Meal styles: pick 5 distinct
  const mealStylesSrc = Array.isArray(variety.mealStylesCoverage) && variety.mealStylesCoverage.length
    ? variety.mealStylesCoverage.map(toValue)
    : Array.isArray(variety.mealStyles) && variety.mealStyles.length
      ? variety.mealStyles.map(toValue)
      : fallbackMealStyles;
  const mealStylesUnique = dedupePreserveOrder(mealStylesSrc);
  const mealStylesCoverage = sampleWeightedDistinct(mealStylesUnique.map(v => ({ value: v, w: 1 })), 5);

  // Return only the values actually used in the prompt
  return {
    cookingPlan: {
      methods: methodsSelected
    },
    mealStylesCoverage
  };
};

/**
 * Builds a HARD CONSTRAINT block for single-cuisine mode.
 * This ensures the LLM generates authentic dishes for the selected cuisine.
 * 
 * @param {string} selectedCuisine - The single selected cuisine (e.g., "Russian")
 * @param {Object} safeInspirations - Filtered inspirations for the cuisine
 * @param {Object} countryData - Country food preferences data (contains cuisineHardConstraints)
 * @returns {Object} { isSingleCuisine: boolean, hardConstraintBlock: string, inspirationsAreHard: boolean, formatExamples: string[] }
 */
const buildCuisineHardConstraint = (selectedCuisine, safeInspirations, countryData, restrictionKeys = []) => {
  // Check if single cuisine is selected (not "Surprise Me" and no comma)
  const isSingleCuisine = selectedCuisine && 
                          selectedCuisine !== 'Surprise Me' && 
                          !selectedCuisine.includes(',');
  
  if (!isSingleCuisine) {
    return { isSingleCuisine: false, hardConstraintBlock: '', inspirationsAreHard: false, formatExamples: null };
  }
  
  const cuisineName = selectedCuisine.trim();
  // Read constraints from countryData.cuisineHardConstraints (migrated from CUISINE_HARD_CONSTRAINTS)
  const constraints = countryData?.cuisineHardConstraints?.[cuisineName];
  
  if (!constraints) {
    // Fallback for cuisines without specific constraints - still enforce basic rules
    return {
      isSingleCuisine: true,
      hardConstraintBlock: `
   SINGLE-CUISINE HARD CONSTRAINT (${cuisineName}):
   - HARD: All 9 meals MUST be recognizably ${cuisineName} home cooking.
   - HARD: Do NOT generate generic European or fusion dishes.
   - HARD: Dish titles and descriptions must reflect ${cuisineName} culinary traditions.
   - HARD: All 9 meals MUST be derived from the inspirations lists below. Do not invent new formats.`,
      inspirationsAreHard: true,
      formatExamples: null
    };
  }
  
  // Build the hard constraint block
  const archetypesList = constraints.archetypes.slice(0, 10).join(', ');
  const filteredFlavorAnchors = filterByRestrictions(constraints.flavorAnchors, restrictionKeys);
  const flavorAnchorsList = filteredFlavorAnchors.join(', ');
  const hardAvoidList = (constraints.hardAvoid || []).slice(0, 25).join(', ');

  // Per-meal-type archetype lines (optional — falls back gracefully when absent)
  const perMealArchetypeLines = [];
  if (constraints.breakfastArchetypes?.length) {
    const minB = constraints.minBreakfastArchetypes ?? 2;
    perMealArchetypeLines.push(`   - HARD: At least ${minB}/3 BREAKFASTS MUST be classic ${cuisineName} breakfast archetypes: ${constraints.breakfastArchetypes.slice(0, 12).join(', ')}.`);
  }
  if (constraints.lunchArchetypes?.length) {
    const minL = constraints.minLunchArchetypes ?? 2;
    perMealArchetypeLines.push(`   - HARD: At least ${minL}/3 LUNCHES MUST be classic ${cuisineName} lunch archetypes: ${constraints.lunchArchetypes.slice(0, 12).join(', ')}.`);
  }
  if (constraints.dinnerArchetypes?.length) {
    const minD = constraints.minDinnerArchetypes ?? 2;
    perMealArchetypeLines.push(`   - HARD: At least ${minD}/3 DINNERS MUST be classic ${cuisineName} dinner archetypes: ${constraints.dinnerArchetypes.slice(0, 12).join(', ')}.`);
  }
  const perMealArchetypesBlock = perMealArchetypeLines.length ? '\n' + perMealArchetypeLines.join('\n') : '';

  // Per-meal-type flavor anchors (optional)
  const perMealAnchorLines = [];
  if (constraints.breakfastFlavorAnchors?.length) {
    perMealAnchorLines.push(`   - HARD: BREAKFAST flavor anchors (use 1-2 per meal): ${filterByRestrictions(constraints.breakfastFlavorAnchors, restrictionKeys).join(', ')}.`);
  }
  if (constraints.lunchFlavorAnchors?.length) {
    perMealAnchorLines.push(`   - HARD: LUNCH flavor anchors (use 1-2 per meal): ${filterByRestrictions(constraints.lunchFlavorAnchors, restrictionKeys).join(', ')}.`);
  }
  if (constraints.dinnerFlavorAnchors?.length) {
    perMealAnchorLines.push(`   - HARD: DINNER flavor anchors (use 1-2 per meal): ${filterByRestrictions(constraints.dinnerFlavorAnchors, restrictionKeys).join(', ')}.`);
  }
  const perMealAnchorsBlock = perMealAnchorLines.length ? '\n' + perMealAnchorLines.join('\n') : '';

  // Breakfast blacklist (optional)
  let breakfastBlacklistLine = '';
  if (constraints.breakfastBlacklist?.length) {
    breakfastBlacklistLine = `\n   - HARD BREAKFAST BLACKLIST (must NOT appear in any of the 3 breakfasts): ${constraints.breakfastBlacklist.join(', ')}.`;
  }

  // Allowed native title words (optional — relaxes the global "no cuisine names in titles" rule)
  let allowedNativeWordsLine = '';
  if (constraints.allowedTitleNativeWords?.length) {
    allowedNativeWordsLine = `\n   - TITLE EXCEPTION: ${cuisineName} canonical dish names ARE allowed in meal titles (e.g. ${constraints.allowedTitleNativeWords.slice(0, 8).join(', ')}). The global "no cuisines/places in titles" rule does NOT apply to these canonical names.`;
  }

  // Use per-meal-type formatExamples bundle if available, else fall back to the flat list
  const hasPerMealFormats = !!(constraints.breakfastFormats?.length || constraints.lunchFormats?.length || constraints.dinnerFormats?.length);
  const formatExamplesPayload = hasPerMealFormats
    ? {
        breakfast: constraints.breakfastFormats || [],
        lunch: constraints.lunchFormats || [],
        dinner: constraints.dinnerFormats || [],
        flat: constraints.formatExamples || []
      }
    : (constraints.formatExamples || null);

  const hardConstraintBlock = `
   SINGLE-CUISINE HARD CONSTRAINT (${cuisineName}):
   - HARD: All 9 meals MUST be recognizably ${cuisineName} home cooking (not fusion or generic European).
   - HARD: All 9 meals MUST be derived from the inspirations lists below. Do not invent new dish formats.
   - HARD: At least ${constraints.minArchetypeMeals}/9 meals MUST be based on classic ${cuisineName} dish archetypes: ${archetypesList}.${perMealArchetypesBlock}
   - HARD: Each meal MUST include at least 1-2 ${cuisineName} flavor anchors: ${flavorAnchorsList}.${perMealAnchorsBlock}
   - HARD AVOID (wrong cuisine markers — do NOT use): ${hardAvoidList}.${breakfastBlacklistLine}${allowedNativeWordsLine}
   - Budget tier ingredient suggestions are SOFT and must only be used if they fit ${cuisineName} cuisine.`;

  return {
    isSingleCuisine: true,
    hardConstraintBlock,
    inspirationsAreHard: true,
    minArchetypeMeals: constraints.minArchetypeMeals,
    formatExamples: formatExamplesPayload,
    allowedTitleNativeWords: constraints.allowedTitleNativeWords || null
  };
};

/**
 * Builds a MULTI-CUISINE constraint block with quota-based distribution.
 * This ensures even distribution when multiple cuisines are selected (50/50, 33/33/33, etc.)
 * 
 * @param {string} selectedCuisine - Comma-separated cuisines (e.g., "Russian, Italian")
 * @param {Object} countryData - Country food preferences data (contains cuisineHardConstraints)
 * @returns {Object} { isMultiCuisine: boolean, multiCuisineBlock: string, cuisineQuotas: Object }
 */
const buildMultiCuisineConstraint = (selectedCuisine, countryData, restrictionKeys = []) => {
  // Check if multiple cuisines are selected
  if (!selectedCuisine || selectedCuisine === 'Surprise Me' || !selectedCuisine.includes(',')) {
    return { isMultiCuisine: false, multiCuisineBlock: '', cuisineQuotas: null };
  }
  
  const cuisines = selectedCuisine.split(',').map(c => c.trim()).filter(Boolean);
  
  if (cuisines.length < 2) {
    return { isMultiCuisine: false, multiCuisineBlock: '', cuisineQuotas: null };
  }
  
  // Get constraints from countryData
  const cuisineHardConstraints = countryData?.cuisineHardConstraints || {};
  
  // Calculate quota distribution (9 meals total)
  const totalMeals = 9;
  const baseMealsPerCuisine = Math.floor(totalMeals / cuisines.length);
  const remainder = totalMeals % cuisines.length;
  
  // Build quotas object and text
  const quotas = {};
  const quotaLines = [];
  
  cuisines.forEach((cuisine, index) => {
    const mealCount = baseMealsPerCuisine + (index < remainder ? 1 : 0);
    quotas[cuisine] = mealCount;
    
    // Get flavor anchors for this cuisine from countryData
    const constraints = cuisineHardConstraints[cuisine];
    const rawAnchors = constraints?.flavorAnchors || [];
    const filteredAnchors = filterByRestrictions(rawAnchors, restrictionKeys);
    const anchors = filteredAnchors.slice(0, 4).join(', ') || 'cuisine-appropriate ingredients';
    
    quotaLines.push(`   - ${cuisine}: exactly ${mealCount} meals (use ${cuisine} anchors: ${anchors})`);
  });
  
  // Build avoid list combining all cuisines' avoid lists, excluding ingredients from selected cuisines
  const selectedAnchors = new Set();
  cuisines.forEach(cuisine => {
    const constraints = cuisineHardConstraints[cuisine];
    if (constraints?.flavorAnchors) {
      constraints.flavorAnchors.forEach(a => selectedAnchors.add(a.toLowerCase()));
    }
  });
  
  const combinedAvoid = new Set();
  cuisines.forEach(cuisine => {
    const constraints = cuisineHardConstraints[cuisine];
    if (constraints?.hardAvoid) {
      constraints.hardAvoid.forEach(item => {
        if (!selectedAnchors.has(item.toLowerCase())) {
          combinedAvoid.add(item);
        }
      });
    }
  });
  
  const avoidList = Array.from(combinedAvoid).slice(0, 12).join(', ');
  
  const multiCuisineBlock = `
   MULTI-CUISINE DISTRIBUTION (${cuisines.join(' + ')}):
   - HARD: Distribute meals evenly across selected cuisines:
${quotaLines.join('\n')}
  - HARD: These cuisine quotas are assigned across the SAME 9 fixed mealPlan slots only. Do not add meals to satisfy a cuisine quota.
  - HARD: Cuisine quotas are global across the full plan, not separate quotas per Breakfast/Lunch/Dinner.
   - Each meal must be clearly identifiable as ONE cuisine (no fusion).
   - Use cuisine-appropriate flavor anchors for each meal based on its assigned cuisine.
   - AVOID (not compatible with selected cuisines): ${avoidList}.`;
  
  return {
    isMultiCuisine: true,
    multiCuisineBlock,
    cuisineQuotas: quotas,
    cuisineCount: cuisines.length
  };
};

// Function to get cuisine-based inspirations
// Now accepts restrictionKeys to filter BEFORE selection, with fallback for variety
const getCuisineInspirations = (selectedCuisine, countryData, restrictionKeys = [], complexityByMealType = {}) => {
  // Debug log
  if (process.env.NODE_ENV !== 'production') {
    console.log('getCuisineInspirations called with:', { selectedCuisine, countryData: countryData?.preferredCuisines, restrictionKeys });
    console.log('inspirationsData keys:', Object.keys(inspirationsData || {}));
  }
  
  // Fisher-Yates shuffle for unbiased randomization
  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  // Helper to get N random items from an array using proper shuffle
  const getRandomItems = (arr, count = 3) => {
    if (!arr || arr.length === 0) return [];
    const shuffled = shuffleArray(arr);
    return shuffled.slice(0, Math.min(count, arr.length));
  };

  // Inspirations may be plain strings or {title, formatGroup, effort} objects.
  // These helpers normalize access without breaking legacy string format.
  const getTitle = (item) => (typeof item === 'string' ? item : (item?.title || ''));
  const getGroup = (item) => (typeof item === 'object' && item?.formatGroup) ? item.formatGroup : null;
  const hasGroups = (arr) => Array.isArray(arr) && arr.some(getGroup);
  // Effort tag: items may carry effort:"involved" (needs notable active work —
  // deep-frying, breading-and-frying, hand-forming dumplings, or constant
  // stovetop attention). Absent == easy (the hands-off baseline). Used to bias
  // sampling toward easy dishes so "easy but tasty" stays honest.
  const isInvolved = (item) => (typeof item === 'object' && item?.effort === 'involved');
  const effortRank = (item) => (isInvolved(item) ? 1 : 0);

  // Format-balanced sampling: pick items from DISTINCT formatGroups before reusing a group.
  // Guarantees that 3 sampled breakfasts won't all be e.g. toasts.
  // Effort bias keeps "easy but tasty" honest:
  //   - effortMode 'strict' (simple complexity): drop involved dishes unless the
  //     easy pool can't fill the requested count (then fall back gracefully).
  //   - effortMode 'relaxed' (standard/advanced): keep all but prefer the easy
  //     option within each format group, so easy dominates and involved dishes
  //     surface only via formats that are involved-only.
  // Falls back to plain random sampling if no formatGroup metadata is present.
  const pickFormatBalanced = (arr, count, effortMode = 'relaxed') => {
    if (!arr?.length) return [];

    // 'strict': prefer an easy-only pool, but fall back to the full list when
    // there aren't enough easy dishes to satisfy the requested count.
    let pool = arr;
    if (effortMode === 'strict') {
      const easyPool = arr.filter(item => !isInvolved(item));
      pool = easyPool.length >= count ? easyPool : arr;
    }

    if (!hasGroups(pool)) {
      // No format metadata: still bias easy-first (Array.sort is stable).
      return shuffleArray(pool)
        .sort((a, b) => effortRank(a) - effortRank(b))
        .slice(0, Math.min(count, pool.length));
    }

    // Group items by formatGroup (items without group go into '__none' bucket).
    const buckets = new Map();
    for (const item of pool) {
      const g = getGroup(item) || '__none';
      if (!buckets.has(g)) buckets.set(g, []);
      buckets.get(g).push(item);
    }
    // Shuffle each bucket, then stable-sort easy-first so within a format the
    // hands-off option is picked before an involved one. Shuffle the bucket
    // order itself for cross-format variety.
    const groupOrder = shuffleArray(Array.from(buckets.keys()));
    for (const g of groupOrder) {
      buckets.set(g, shuffleArray(buckets.get(g)).sort((a, b) => effortRank(a) - effortRank(b)));
    }

    // Round-robin: pick one from each group in shuffled order, repeat until count reached.
    const selected = [];
    while (selected.length < count) {
      let progressed = false;
      for (const g of groupOrder) {
        if (selected.length >= count) break;
        const bucket = buckets.get(g);
        if (bucket.length) {
          selected.push(bucket.shift());
          progressed = true;
        }
      }
      if (!progressed) break; // all buckets exhausted
    }
    return selected;
  };

  // Map a meal type's cooking complexity to an effort-bias mode for sampling.
  // 'simple' → strict (hands-off only, with fallback); else relaxed (easy-first).
  const effortModeFor = (mealType) => (
    (complexityByMealType?.[mealType] || 'standard') === 'simple' ? 'strict' : 'relaxed'
  );

  // Pre-filter inspirations for a cuisine and meal type
  // Returns { filtered: [], unfiltered: [] } for fallback support.
  // Items may be strings or {title, formatGroup} objects — we filter by title text.
  const getFilteredInspirations = (cuisine, mealType) => {
    const unfiltered = inspirationsData[cuisine]?.[mealType] || [];
    if (!restrictionKeys.length) {
      return { filtered: unfiltered, unfiltered };
    }
    // filterByRestrictions expects strings; map to titles for the predicate, keep originals.
    const titles = unfiltered.map(getTitle);
    const allowedTitles = new Set(filterByRestrictions(titles, restrictionKeys));
    const filtered = unfiltered.filter(item => allowedTitles.has(getTitle(item)));
    return { filtered, unfiltered };
  };

  // Select items with fallback: if filtered < minCount, add unfiltered items.
  // Uses format-balanced picking when items have formatGroup metadata.
  const selectWithFallback = (cuisine, mealType, count, minCount = 3) => {
    const { filtered, unfiltered } = getFilteredInspirations(cuisine, mealType);
    const effortMode = effortModeFor(mealType);

    // First, try to get 'count' items from filtered (format-balanced if applicable).
    let selected = pickFormatBalanced(filtered, count, effortMode);

    // If we have less than minCount, add from unfiltered (excluding already selected).
    if (selected.length < minCount && unfiltered.length > 0 && !restrictionKeys.length) {
      const selectedTitles = new Set(selected.map(getTitle));
      const remaining = unfiltered.filter(item => !selectedTitles.has(getTitle(item)));
      const needed = minCount - selected.length;
      const additional = pickFormatBalanced(remaining, needed, effortMode);
      selected = [...selected, ...additional];

      if (process.env.NODE_ENV !== 'production' && additional.length > 0) {
        console.log(`Fallback: added ${additional.length} unfiltered ${mealType} inspirations for ${cuisine}`);
      }
    }

    return selected;
  };

  // Helper to calculate weighted cuisine distribution
  const getWeightedCuisineDistribution = (preferredCuisines, totalSlots = 3) => {
    const distribution = {};
    const totalWeight = preferredCuisines.reduce((sum, c) => sum + (c.w || 0), 0);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('getWeightedCuisineDistribution input:', { preferredCuisines, totalSlots, totalWeight });
    }
    
    // Calculate base counts and remainders
    const allocations = preferredCuisines.map(cuisine => {
      const exactCount = (cuisine.w / totalWeight) * totalSlots;
      const baseCount = Math.floor(exactCount);
      const remainder = exactCount - baseCount;
      return { cuisine: cuisine.v, baseCount, remainder, exactCount };
    });
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('allocations:', allocations);
    }
    
    // Assign base counts
    allocations.forEach(alloc => {
      distribution[alloc.cuisine] = alloc.baseCount;
    });
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('distribution after base assignment:', distribution);
    }
    
    // Calculate remaining slots
    let remaining = totalSlots - allocations.reduce((sum, a) => sum + a.baseCount, 0);
    
    // Add random tiebreaker before sorting to avoid bias
    const withTiebreaker = allocations.map(alloc => ({
      ...alloc,
      // Weight remainder by cuisine weight for probability
      probability: alloc.remainder * (alloc.exactCount / totalSlots),
      tiebreaker: Math.random() // Pre-generate random value for consistent sorting
    }));
    
    // Sort by probability descending, using tiebreaker for close values
    const sorted = withTiebreaker.sort((a, b) => {
      const probDiff = b.probability - a.probability;
      if (Math.abs(probDiff) < 0.1) {
        // If probabilities are close, use pre-generated tiebreaker
        return a.tiebreaker - b.tiebreaker;
      }
      return probDiff;
    });
    
    // Distribute remaining slots
    for (let i = 0; i < remaining && i < sorted.length; i++) {
      const cuisine = sorted[i].cuisine;
      // Use weighted random to decide if this cuisine gets an extra slot
      const shouldAllocate = Math.random() < sorted[i].probability / sorted[0].probability;
      if (shouldAllocate || i < remaining) {
        distribution[cuisine] = (distribution[cuisine] || 0) + 1;
      }
    }
    
    // Ensure we've allocated all slots
    const allocated = Object.values(distribution).reduce((sum, count) => sum + count, 0);
    if (allocated < totalSlots) {
      // Add remaining to the highest weighted cuisine
      const topCuisine = allocations[0].cuisine;
      distribution[topCuisine] = (distribution[topCuisine] || 0) + (totalSlots - allocated);
    }
    
    return distribution;
  };

  // Helper to select inspirations based on distribution
  // Now uses pre-filtering with fallback for variety
  const selectInspirationsFromDistribution = (distribution, mealType, minTotal = 3) => {
    const selected = [];
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('selectInspirationsFromDistribution:', { distribution, mealType, hasInspirations: !!inspirationsData });
    }
    
    // First pass: collect filtered inspirations from each cuisine
    Object.entries(distribution).forEach(([cuisine, count]) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Checking cuisine: ${cuisine}, count: ${count}, exists: ${!!inspirationsData[cuisine]}, hasMealType: ${!!(inspirationsData[cuisine] && inspirationsData[cuisine][mealType])}`);
      }
      
      if (count > 0 && inspirationsData[cuisine] && inspirationsData[cuisine][mealType]) {
        // Use selectWithFallback for each cuisine
        const items = selectWithFallback(cuisine, mealType, count, Math.min(count, 2));
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Selected ${items.length} items from ${cuisine} ${mealType}:`, items);
        }
        selected.push(...items);
      }
    });
    
    // Second pass: if total < minTotal, add more from any available cuisine
    if (selected.length < minTotal) {
      const allCuisines = Object.keys(distribution);
      for (const cuisine of allCuisines) {
        if (selected.length >= minTotal) break;
        
        const { filtered, unfiltered } = getFilteredInspirations(cuisine, mealType);
        const pool = filtered.length > 0 || restrictionKeys.length ? filtered : unfiltered;
        const selectedTitles = new Set(selected.map(getTitle));
        const remaining = pool.filter(item => !selectedTitles.has(getTitle(item)));
        const needed = minTotal - selected.length;
        const additional = pickFormatBalanced(remaining, needed, effortModeFor(mealType));
        selected.push(...additional);
        
        if (process.env.NODE_ENV !== 'production' && additional.length > 0) {
          console.log(`Global fallback: added ${additional.length} ${mealType} inspirations from ${cuisine}`);
        }
      }
    }
    
    // Shuffle the final list to mix cuisines, then unwrap to plain title strings.
    return shuffleArray(selected).map(getTitle);
  };

  // Main logic
  if (selectedCuisine === "Surprise Me") {
    // Use weighted distribution from country preferences
    const rawPreferredCuisines = countryData?.preferredCuisines || [];
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('rawPreferredCuisines:', rawPreferredCuisines);
      console.log('First cuisine:', rawPreferredCuisines[0]);
    }
    
    // Normalize to object format: handle both string arrays and object arrays
    const preferredCuisines = rawPreferredCuisines.map((item, index) => {
      if (typeof item === 'string') {
        // Old format: string like "Spanish (50%)" or just "Spanish"
        // Parse the string to extract cuisine name and weight
        const match = item.match(/^(.+?)\s*\((\d+)%\)$/);
        if (match) {
          return { v: match[1].trim(), w: parseInt(match[2], 10) };
        } else {
          // No weight specified, assign equal weights
          return { v: item.trim(), w: Math.round(100 / rawPreferredCuisines.length) };
        }
      } else if (typeof item === 'object' && item.v && item.w) {
        // New format: already in object format
        return item;
      } else {
        // Unknown format, skip
        return null;
      }
    }).filter(Boolean);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('Normalized preferredCuisines:', preferredCuisines);
    }
    
    if (preferredCuisines.length === 0) {
      // Fallback to American if no preferences
      const americanBreakfast = getFilteredInspirations('American', 'breakfast').filtered;
      const americanLunch = getFilteredInspirations('American', 'lunch').filtered;
      const americanDinner = getFilteredInspirations('American', 'dinner').filtered;

      return {
        breakfastInspirations: getRandomItems(americanBreakfast, 3),
        lunchInspirations: getRandomItems(americanLunch, 3),
        dinnerInspirations: getRandomItems(americanDinner, 3)
      };
    }
    
    // Calculate distribution for each meal type
    const breakfastDist = getWeightedCuisineDistribution(preferredCuisines, 3);
    const lunchDist = getWeightedCuisineDistribution(preferredCuisines, 3);
    const dinnerDist = getWeightedCuisineDistribution(preferredCuisines, 3);
    
    return {
      breakfastInspirations: selectInspirationsFromDistribution(breakfastDist, 'breakfast'),
      lunchInspirations: selectInspirationsFromDistribution(lunchDist, 'lunch'),
      dinnerInspirations: selectInspirationsFromDistribution(dinnerDist, 'dinner')
    };
  } else {
    // User selected specific cuisine(s)
    // selectedCuisine could be "Italian" or "Italian, Spanish"
    const cuisines = selectedCuisine.split(',').map(c => c.trim()).filter(Boolean);
    
    if (cuisines.length === 0) {
      // Fallback
      return {
        breakfastInspirations: [],
        lunchInspirations: [],
        dinnerInspirations: []
      };
    }
    
    // Equal distribution among selected cuisines
    // Breakfast/Lunch/Dinner: 3 inspirations each
    // Now uses pre-filtering with fallback
    const getFromMultipleCuisines = (mealType) => {
      const totalCount = 3; // 3 inspirations for all meal types
      const minCount = 3; // Minimum inspirations to guarantee variety
      const perCuisine = Math.floor(totalCount / cuisines.length);
      const remainder = totalCount % cuisines.length;
      const selected = [];
      
      // First pass: collect filtered inspirations from each cuisine
      cuisines.forEach((cuisine, index) => {
        const count = perCuisine + (index < remainder ? 1 : 0);
        if (inspirationsData[cuisine] && inspirationsData[cuisine][mealType]) {
          const items = selectWithFallback(cuisine, mealType, count, Math.min(count, 2));
          selected.push(...items);
        }
      });
      
      // Second pass: if total < minCount, add more from any available cuisine
      if (selected.length < minCount) {
        for (const cuisine of cuisines) {
          if (selected.length >= minCount) break;
          
          const { filtered, unfiltered } = getFilteredInspirations(cuisine, mealType);
          const pool = filtered.length > 0 || restrictionKeys.length ? filtered : unfiltered;
          const selectedTitles = new Set(selected.map(getTitle));
          const remaining = pool.filter(item => !selectedTitles.has(getTitle(item)));
          const needed = minCount - selected.length;
          const additional = pickFormatBalanced(remaining, needed, effortModeFor(mealType));
          selected.push(...additional);
        }
      }
      
      return shuffleArray(selected).map(getTitle);
    };
    
    return {
      breakfastInspirations: getFromMultipleCuisines('breakfast'),
      lunchInspirations: getFromMultipleCuisines('lunch'),
      dinnerInspirations: getFromMultipleCuisines('dinner')
    };
  }
};

// Helper function to format dietary preferences for prompts
// Returns { text: string, hasRestrictions: boolean, restrictionKeys: string[] }
// Separates strict restrictions from soft diet style preferences for clarity
const formatDietaryPreferences = (dietaryPreferences) => {
  // Handle both old string format and new object array format
  if (typeof dietaryPreferences === 'string') {
    // Check if string contains meaningful restrictions (not just "none" or empty)
    const trimmed = dietaryPreferences.trim().toLowerCase();
    const hasRestrictions = trimmed.length > 0 && 
      !trimmed.includes('none') && 
      !trimmed.includes('no restrictions') &&
      !trimmed.includes('no specific');
    const restrictionKeys = inferRestrictionKeysFromDietaryText(dietaryPreferences);
    return {
      text: hasRestrictions ? `- Dietary restrictions: ${dietaryPreferences} (strict).` : '',
      hasRestrictions,
      restrictionKeys,
      restrictionLabels: restrictionKeys,
    };
  }
  
  if (Array.isArray(dietaryPreferences)) {
    // Enrich objects: restore missing fields (type, isNoneOption) from canonical source.
    // Backend serialization strips these fields, so we look them up by key.
    const enriched = dietaryPreferences.map(pref => {
      if (!pref?.key) return pref;
      const canonical = DIETARY_OPTIONS_BY_KEY[pref.key];
      if (!canonical) return pref;
      return {
        ...pref,
        type: pref.type || canonical.type,
        isNoneOption: pref.isNoneOption ?? canonical.isNoneOption,
        isAdvanced: pref.isAdvanced ?? canonical.isAdvanced,
        label: pref.label || canonical.label,
        description: pref.description || canonical.description,
      };
    });

    // Filter out "none" options
    const activePreferences = enriched.filter(pref => 
      pref && pref.key && !pref.isNoneOption && pref.key !== 'none' && pref.key !== 'noneFood' && pref.key !== 'noneDiet'
    );
    
    if (activePreferences.length === 0) {
      return { text: '', hasRestrictions: false, restrictionKeys: [] };
    }
    
    // Separate hard restrictions from soft diet style preferences. Some diet styles
    // (vegetarian/vegan) are also strict ingredient bans, not just flavor guidance.
    const foodRestrictions = activePreferences.filter(pref => pref.type === 'food');
    const strictDietStyleRestrictions = activePreferences.filter(pref => STRICT_DIET_STYLE_RESTRICTION_KEYS.has(pref.key));
    const strictRestrictions = [...foodRestrictions, ...strictDietStyleRestrictions];
    const dietStyles = activePreferences.filter(pref => pref.type === 'diet' && !STRICT_DIET_STYLE_RESTRICTION_KEYS.has(pref.key));
    
    const parts = [];
    
    // Strict restrictions are hard requirements
    if (strictRestrictions.length > 0) {
      const formattedRestrictions = strictRestrictions.map(pref => 
        `${pref.label} (${pref.description})`
      ).join(', ');
      parts.push(`- Dietary restrictions (STRICT - must avoid): ${formattedRestrictions}.`);
    }
    
    // Diet styles are preferences that guide recipe selection
    if (dietStyles.length > 0) {
      const formattedStyles = dietStyles.map(pref => 
        `${pref.label} (${pref.description})`
      ).join(', ');
      parts.push(`- Diet style preference: ${formattedStyles}.`);
    }

    // hasRestrictions is true if there are any hard restrictions (which require swap logic)
    const hasRestrictions = strictRestrictions.length > 0;
    
    // Extract restriction keys for generating specific swap examples
    const restrictionKeys = strictRestrictions.map(pref => pref.key);
    
    // Extract human-readable labels for the prompt header (e.g., "Dairy Free", "No Nuts")
    const restrictionLabels = strictRestrictions.map(pref => pref.label);
    
    return { text: parts.join('\n   '), hasRestrictions, restrictionKeys, restrictionLabels };
  }
  
  return { text: '', hasRestrictions: false, restrictionKeys: [] };
};

// Export the budget goal determination function for use in grocery processing
export { determineBudgetGoal };

/**
 * Generates a complete meal plan prompt for the LLM.
 * 
 * @param {Object} params - Configuration parameters
 * @param {string} params.userCountry - User's country code (e.g., "Spain", "USA")
 * @param {Object|number} params.userBudget - Budget object with {key, label, min, max} or raw number
 * @param {number} params.numberOfPeople - Number of people to cook for
 * @param {string} params.selectedCuisine - Selected cuisine or "Surprise Me"
 * @param {Array|string} params.dietaryPreferences - Dietary restrictions and preferences
 * @param {Object} params.cookingStyle - Cooking complexity and batch settings per meal type
 * @returns {Promise<string>} The complete prompt string for meal plan generation
 */
export const generateMealPlanPrompt = async ({
  userCountry,
  userBudget,
  numberOfPeople,
  selectedCuisine,
  dietaryPreferences,
  cookingStyle
}) => {

  let effectiveCountry = userCountry;
  
  if (!effectiveCountry || typeof effectiveCountry !== 'string') {
    try {
      // Await the async function
      effectiveCountry = await getUserCountry();
      if (process.env.NODE_ENV !== 'production') {
        console.debug(`No userCountry provided or invalid, using global setting: ${effectiveCountry}`);
      }
    } catch (error) {
      console.error('Error getting country:', error);
      effectiveCountry = DEFAULT_COUNTRY;
    }
  } else {
    if (process.env.NODE_ENV !== 'production') {
      console.debug(`Using provided userCountry: ${effectiveCountry}`);
    }
  }
  
  // Further fallback if needed
  if (!effectiveCountry || !isCountrySupported(effectiveCountry)) {
    console.warn(`Country ${effectiveCountry} not supported, falling back to ${DEFAULT_COUNTRY}`);
    effectiveCountry = DEFAULT_COUNTRY;
  }
  
  const countryData = countryFoodPreferences[effectiveCountry] || countryFoodPreferences[DEFAULT_COUNTRY];
  
  // Debug log raw country data
  if (process.env.NODE_ENV !== 'production') {
    console.log('Raw countryData.preferredCuisines:', countryData?.preferredCuisines);
  }
  
  // Get country's currency info
  const currency = getCountryCurrency(effectiveCountry);
  const isMetric = usesMetricSystem(effectiveCountry);
  if (process.env.NODE_ENV !== 'production') {
    console.debug(`Using currency for ${effectiveCountry}: ${currency.code} (${currency.symbol})`);
  }
  const currencySymbol = currency.symbol;

  const mealSettings = defineSelectedCuisines(selectedCuisine, countryData);
  // If userBudget is an object, use label for display and min for calculations
  const displayBudget = typeof userBudget === 'object' && userBudget !== null ? userBudget.label : userBudget;
  
  // Get budget range from country_food_preferences based on budget goal key
  const budgetKey = typeof userBudget === 'object' && userBudget !== null && userBudget.key 
    ? userBudget.key.toLowerCase() 
    : 'standard';
  const countryBudgetRange = countryData?.budget?.[budgetKey];
  
  // Format cleanDisplayBudget as range from country preferences (e.g., "45€ - 85€")
  let cleanDisplayBudget;
  let budgetMin = null;
  let budgetMax = null;
  
  if (countryBudgetRange && Array.isArray(countryBudgetRange)) {
    const [min, max] = countryBudgetRange;
    budgetMin = min;
    budgetMax = max;
    if (max === null) {
      cleanDisplayBudget = `${currencySymbol}${min}+`;
    } else {
      cleanDisplayBudget = `${currencySymbol}${min} - ${currencySymbol}${max}`;
    }
  } else {
    // Fallback to extracting from displayBudget
    cleanDisplayBudget = typeof displayBudget === 'string' 
      ? displayBudget.replace(/^[^0-9]*/, '').split('•')[0].trim() 
      : displayBudget;
  }

  const budgetValue = typeof userBudget === 'object' && userBudget !== null ? userBudget.min : userBudget;
  
  // Generate variety elements for unique meal plans
  const varietySeed = generateVarietySeed();
  const varietyPrompts = getVarietyPrompts(varietySeed, countryData);

  // Format dietary preferences FIRST to get restrictionKeys for filtering inspirations
  const formattedDietaryPreferences = formatDietaryPreferences(dietaryPreferences);
  const restrictionKeys = formattedDietaryPreferences.restrictionKeys || [];
  const excludesDairy = shouldExcludeDairy(restrictionKeys);
  const excludesEggs = restrictionKeys.some(key => ['noEggs', 'vegan'].includes(key));
  const excludesGluten = restrictionKeys.some(key => key === 'glutenFree');

  // Per-mealType cooking complexity, used to bias inspiration sampling toward
  // easy/hands-off dishes ('simple' = strict; standard/advanced = easy-first).
  const complexityByMealType = {
    breakfast: cookingStyle?.breakfast?.cookingTime || 'standard',
    lunch: cookingStyle?.lunch?.cookingTime || 'standard',
    dinner: cookingStyle?.dinner?.cookingTime || 'standard'
  };

  // Get cuisine-based inspirations (now filters internally with fallback)
  const cuisineInspirations = getCuisineInspirations(selectedCuisine, countryData, restrictionKeys, complexityByMealType);
  
  // Debug logging
  if (process.env.NODE_ENV !== 'production') {
    console.log('Selected Cuisine:', selectedCuisine);
    console.log('Restriction Keys:', restrictionKeys);
    console.log('Cuisine Inspirations:', cuisineInspirations);
    console.log('Breakfast count:', cuisineInspirations?.breakfastInspirations?.length || 0);
    console.log('Lunch count:', cuisineInspirations?.lunchInspirations?.length || 0);
    console.log('Dinner count:', cuisineInspirations?.dinnerInspirations?.length || 0);
  }
  
  // safeInspirations now comes directly from getCuisineInspirations (already filtered with fallback)
  let safeInspirations = {
    breakfastInspirations: removeOvernightInspirations(cuisineInspirations?.breakfastInspirations || []),
    lunchInspirations: removeOvernightInspirations(cuisineInspirations?.lunchInspirations || []),
    dinnerInspirations: removeOvernightInspirations(cuisineInspirations?.dinnerInspirations || [])
  };

  const breakfastComplexityForInspirations = cookingStyle?.breakfast?.cookingTime || 'standard';
  const breakfastLimitForInspirations = { simple: 15, standard: 20, advanced: 25 }[breakfastComplexityForInspirations] || 20;
  const enforceQuickBreakfastPolicy = breakfastLimitForInspirations < 35;

  if (enforceQuickBreakfastPolicy) {
    safeInspirations = {
      ...safeInspirations,
      breakfastInspirations: applyQuickBreakfastPolicy(
        safeInspirations.breakfastInspirations,
        selectedCuisine,
        countryData,
        restrictionKeys
      )
    };
  }

  // Build cuisine hard constraint for single-cuisine mode (reads from countryData.cuisineHardConstraints)
  const cuisineHardConstraint = buildCuisineHardConstraint(selectedCuisine, safeInspirations, countryData, restrictionKeys);
  
  // Build multi-cuisine constraint for multiple cuisines (50/50, 33/33/33, etc.)
  const multiCuisineConstraint = buildMultiCuisineConstraint(selectedCuisine, countryData, restrictionKeys);

  // Build Section 5 format-examples lines, supporting per-meal-type payload (new) or flat array (legacy).
  const buildFormatExamplesLines = () => {
    const fe = cuisineHardConstraint.formatExamples;
    if (fe && typeof fe === 'object' && !Array.isArray(fe)) {
      const lines = [];
      const breakfastExamples = enforceQuickBreakfastPolicy
        ? removeOvernightInspirations(fe.breakfast).filter(example => !SLOW_BREAKFAST_PATTERN.test(example))
        : removeOvernightInspirations(fe.breakfast);
      const lunchExamples = removeOvernightInspirations(fe.lunch);
      const dinnerExamples = removeOvernightInspirations(fe.dinner);
      if (breakfastExamples?.length) lines.push(`   - Breakfast format examples: ${breakfastExamples.join(', ')}.`);
      if (lunchExamples?.length) lines.push(`   - Lunch format examples: ${lunchExamples.join(', ')}.`);
      if (dinnerExamples?.length) lines.push(`   - Dinner format examples: ${dinnerExamples.join(', ')}.`);
      const flatExamples = removeOvernightInspirations(fe.flat);
      if (!lines.length && flatExamples?.length) lines.push(`   - Format examples: ${flatExamples.join(', ')}.`);
      return lines.join('\n');
    }
    const flat = Array.isArray(fe) && fe.length ? fe.join(', ') : varietyPrompts.mealStylesCoverage.join(', ');
    return `   - Format examples: ${flat}.`;
  };
  const formatExamplesLines = buildFormatExamplesLines();

  // Title rule — when single-cuisine mode allows native dish words, append an exception clause.
  // IMPORTANT: the exception covers only the listed canonical dish words themselves (e.g. "tortilla", "gazpacho"),
  // NOT the demonym/adjective form of the cuisine (e.g. "Spanish", "Greek", "Italian"). Demonyms remain forbidden.
  const allowedNativeWords = cuisineHardConstraint.allowedTitleNativeWords;
  const titleDemonymExample = excludesGluten
    ? 'write "Paprika Potato Skillet" not "Spanish Potato Skillet"; write "Tomato Basil Rice" not "Italian Tomato Rice"'
    : (excludesEggs
      ? 'write "Paprika Potato Skillet" not "Spanish Potato Skillet"; write "Tomato Basil Pasta" not "Italian Tomato Pasta"'
      : (excludesDairy
        ? 'write "Tortilla Francesa with Parsley" not "Spanish Omelette with Parsley"; write "Tomato Basil Pasta" not "Italian Tomato Pasta"'
        : 'write "Tortilla Francesa with Parsley" not "Spanish Omelette with Parsley"; write "Yogurt Bowl with Honey" not "Greek Yogurt Bowl with Honey"'));
  const titleNativeException = allowedNativeWords?.length
    ? ` EXCEPTION: only these canonical ${(selectedCuisine || '').trim()} dish words ARE allowed verbatim in titles: ${allowedNativeWords.slice(0, 6).join(', ')}. The demonym/adjective form of the cuisine (e.g. "${(selectedCuisine || '').trim()}", "Greek", "Italian", "French", "Mediterranean") is STILL FORBIDDEN in titles and descriptions — ${titleDemonymExample}.`
    : '';
  
  // Debug: log hard constraint
  if (process.env.NODE_ENV !== 'production' && cuisineHardConstraint.isSingleCuisine) {
    console.log('Single cuisine mode activated:', selectedCuisine);
    console.log('Inspirations are HARD:', cuisineHardConstraint.inspirationsAreHard);
  }
  
  if (process.env.NODE_ENV !== 'production' && multiCuisineConstraint.isMultiCuisine) {
    console.log('Multi-cuisine mode activated:', selectedCuisine);
    console.log('Cuisine quotas:', multiCuisineConstraint.cuisineQuotas);
  }

  // Dynamically determine Budget Goal
  // Use the key from userBudget object if available (capitalized), otherwise extract from label or calculate
  const budgetGoal = typeof userBudget === 'object' && userBudget !== null && userBudget.key
    ? userBudget.key.charAt(0).toUpperCase() + userBudget.key.slice(1).toLowerCase()
    : (typeof displayBudget === 'string' && displayBudget.includes('•') 
        ? displayBudget.split('•')[1].trim() 
        : determineBudgetGoal(budgetValue, effectiveCountry, numberOfPeople));
  
  // Get budget explanation with restriction-filtered ingredient hints and cuisine kit
  const budgetExplanation = getBudgetGoalExplanation(budgetGoal, countryData, restrictionKeys, selectedCuisine);

  // Debug: log cuisine kit application
  if (process.env.NODE_ENV !== 'production') {
    if (budgetExplanation.cuisineAnchors || budgetExplanation.flavorAnchors?.length) {
      console.log('Cuisine Kit applied for:', selectedCuisine);
      console.log('Flavor anchors:', budgetExplanation.flavorAnchors);
      console.log('Avoid list:', budgetExplanation.cuisineAvoid);
    }
  }

  // Cooking time limits based on complexity level.
  // Single source of truth: src/utils/cookingTimeLimits.js (shared with the
  // runtime validator in useMealPlanLoader.js). Do NOT redefine locally — a
  // divergent copy makes the model produce valid-per-prompt times that the
  // validator then rejects, hard-failing the whole generation.

  // Default cooking style if not provided
  const defaultCookingStyle = {
    breakfast: { cookingTime: 'standard', cookFor2Days: false },
    lunch: { cookingTime: 'standard', cookFor2Days: false },
    dinner: { cookingTime: 'standard', cookFor2Days: false }
  };

  // Merge with defaults for any missing values
  const effectiveCookingStyle = {
    breakfast: { ...defaultCookingStyle.breakfast, ...cookingStyle?.breakfast },
    lunch: { ...defaultCookingStyle.lunch, ...cookingStyle?.lunch },
    dinner: { ...defaultCookingStyle.dinner, ...cookingStyle?.dinner }
  };

  // Get time limits based on user's complexity selection
  const breakfastTimeLimit = COOKING_TIME_LIMITS.breakfast[effectiveCookingStyle.breakfast.cookingTime] || 20;
  const lunchTimeLimit = COOKING_TIME_LIMITS.lunch[effectiveCookingStyle.lunch.cookingTime] || 35;
  const dinnerTimeLimit = COOKING_TIME_LIMITS.dinner[effectiveCookingStyle.dinner.cookingTime] || 45;
  // Passive-time allowance: unattended oven/simmer time may extend the TOTAL
  // cookingTime beyond the active cap. Breakfast gets no bonus (stays quick).
  const lunchPassiveBonus = getPassiveCookingBonus('lunch');
  const dinnerPassiveBonus = getPassiveCookingBonus('dinner');
  const lunchTimeLimitTotal = lunchTimeLimit + lunchPassiveBonus;
  const dinnerTimeLimitTotal = dinnerTimeLimit + dinnerPassiveBonus;
  const quickBreakfastFormats = [
    'quick no-cook fruit bowl',
    excludesGluten ? 'rice-cake/corn-tortilla' : 'toast',
    'vegetable or bean spread',
    excludesEggs ? null : 'egg/simple omelette',
    excludesDairy ? null : 'yogurt/cheese',
  ].filter(Boolean).join('/');
  const quickBreakfastReplacementFormats = `${quickBreakfastFormats} format from the same cuisine`;
  const quickBreakfastHardRule = breakfastTimeLimit < 35
    ? `
  - BREAKFAST TIME HARD RULE: every Breakfast cookingTime MUST be <= ${breakfastTimeLimit}. Do NOT choose baked/set/proved/chilled-set breakfast formats, overnight oats, chia pudding, chia bowls that require soaking/setting/chilling/passive waiting, or anything requiring oven baking, batter baking, sponge cake, bizcocho, magdalenas, churros, from-scratch tortilla de patatas, dough, or passive waiting. If an inspiration conflicts with this time limit, replace it with a ${quickBreakfastReplacementFormats}.`
    : '';
  // NOTE: the old "SIMPLE LUNCH HARD RULE" that banned baked casseroles/oven
  // lunches was removed — it only fired at lunchTimeLimit <= 20 (never, since the
  // lunch minimum is 25) and its content now contradicts the passive-time model,
  // where oven/braise lunches are intentionally allowed within the total cap.
  const noOvernightRecipeRule = `
  - ABSOLUTE OVERNIGHT RECIPE BAN: do NOT output any recipe whose title, description, or required method uses overnight / night-before prep, overnight oats, soaked oats, overnight chia, overnight marinades, or components soaked overnight. Use same-day supermarket-ready ingredients and same-day methods instead.`;
  const hardCookingTimeSelfCheck = `(f) HARD COOKING TIME CHECK: every meal's total cookingTime must stay within its exact mealType limit: Breakfast <= ${breakfastTimeLimit}, Lunch <= ${lunchTimeLimitTotal}, Dinner <= ${dinnerTimeLimitTotal}. If any meal exceeds its limit by even 1 minute, simplify the technique, use supermarket-ready/pre-cooked components, or replace the concept before output. Also reject any overnight/night-before recipe concept outright — do not fix it by increasing cookingTime.${breakfastTimeLimit < 35 ? ` For Breakfast, if any dish is baked, sponge cake/bizcocho, churros, from-scratch tortilla de patatas, dough-based, chia pudding, chia bowl requiring soaking/setting/chilling/passive waiting, chilled/set, or otherwise longer than ${breakfastTimeLimit} min, replace it with a ${quickBreakfastReplacementFormats} breakfast.` : ''}`;

  // Generate complexity descriptions for the prompt
  // NOTE: Complexity affects ONLY cooking method/technique and time, NOT ingredient cost or quality.
  // Ingredient selection is driven solely by the budget tier.
  // IMPORTANT: descriptions intentionally contain NO concrete dish examples.
  // Concrete dish ideas must come from cuisine archetypes / inspirations / formatExamples
  // (which are cuisine-aware). Embedding dish names here leaks foreign cuisine cues
  // (e.g. "filled crêpes", "homemade pasta with sauce", "quesadillas") into single-cuisine
  // prompts and produced fusion outputs (ravioli for Spanish breakfast).
  const getComplexityDescription = (mealType, complexity) => {
    const descriptions = {
      simple: {
        breakfast: 'fast and effortless — no-cook assembly, toasting, or boiling; minimal hands-on attention',
        lunch: 'fast and effortless — assembly, tossed salad/bowl/wrap, canned legumes, ready grains, a quick oven/sheet-pan tray, or short one-pan cooking; minimal hands-on work',
        dinner: 'fast and effortless — sheet-pan, oven-bake, or one-pot; mostly passive with minimal hands-on work'
      },
      standard: {
        breakfast: 'still easy and hands-off, with a little more cooking than the quickest option; simple common techniques, no fussy multi-step components',
        lunch: 'still easy and hands-off, with a bit more cooking time; favour oven/sheet-pan roasting or one-pot simmering, or simple assembly with a tasty sauce/side — never active stovetop juggling',
        dinner: 'still easy and hands-off, with a bit more cooking time; favour roasting, baking, sheet-pan or one-pot meals over constant stirring or multiple active pans'
      },
      advanced: {
        breakfast: 'still easy and hands-off — the extra time only buys richer flavour, never chef technique or stressful multitasking',
        lunch: 'still easy and hands-off — use the extra time for deeper flavour via mostly-passive methods (a longer roast/bake or one-pot simmer), NOT more active steps or multi-pan juggling',
        dinner: 'still easy and hands-off — use the extra time for deeper flavour via slow oven-braising, roasting, or one-pot simmering; long UNATTENDED cooking, not more active work'
      }
    };
    return descriptions[complexity]?.[mealType] || descriptions.standard[mealType];
  };

  // Build complexity instructions for prompt
  const buildComplexityInstructions = () => {
    const instructions = [];
    
    const breakfastComplexity = effectiveCookingStyle.breakfast.cookingTime;
    const lunchComplexity = effectiveCookingStyle.lunch.cookingTime;
    const dinnerComplexity = effectiveCookingStyle.dinner.cookingTime;

    // Track if any meal has advanced complexity
    const hasAdvancedComplexity = breakfastComplexity === 'advanced' || 
                                   lunchComplexity === 'advanced' || 
                                   dinnerComplexity === 'advanced';

    // Only add specific instructions if not all standard
    if (breakfastComplexity !== 'standard') {
      instructions.push(`- Breakfast complexity: ${breakfastComplexity} — ${getComplexityDescription('breakfast', breakfastComplexity)}`);
    }
    if (lunchComplexity !== 'standard') {
      instructions.push(`- Lunch complexity: ${lunchComplexity} — ${getComplexityDescription('lunch', lunchComplexity)}`);
    }
    if (dinnerComplexity !== 'standard') {
      instructions.push(`- Dinner complexity: ${dinnerComplexity} — ${getComplexityDescription('dinner', dinnerComplexity)}`);
    }

    return { text: instructions.join('\n'), hasAdvancedComplexity };
  };

  const complexityResult = buildComplexityInstructions();
  const complexityInstructions = complexityResult.text;
  const hasAdvancedComplexity = complexityResult.hasAdvancedComplexity;

  // Format cooking style instructions for the prompt (batch cooking yields)
  const formatCookingStyleInstructions = () => {
    const instructions = [];
    const hasBatchYields = effectiveCookingStyle.breakfast.cookFor2Days || 
                           effectiveCookingStyle.lunch.cookFor2Days || 
                           effectiveCookingStyle.dinner.cookFor2Days;
    
    // Add batch yield clarification if any meal has batch cooking
    if (hasBatchYields) {
      instructions.push(`- When a mealType has a specified batch yield below, ingredients quantities MUST reflect the full batch yield, not a single serving.`);
    }
    
    // Calculate actual yields for batch cooking
    if (effectiveCookingStyle.breakfast.cookFor2Days) {
      const yieldCount = numberOfPeople * 2;
      instructions.push(`- Breakfast recipes: yield ${yieldCount} servings (${numberOfPeople} person${numberOfPeople > 1 ? 's' : ''} × 2 days).`);
    }
    if (effectiveCookingStyle.lunch.cookFor2Days) {
      const yieldCount = numberOfPeople * 2;
      instructions.push(`- Lunch recipes: yield ${yieldCount} servings (${numberOfPeople} person${numberOfPeople > 1 ? 's' : ''} × 2 days).`);
    }
    if (effectiveCookingStyle.dinner.cookFor2Days) {
      const yieldCount = numberOfPeople * 2;
      instructions.push(`- Dinner recipes: yield ${yieldCount} servings (${numberOfPeople} person${numberOfPeople > 1 ? 's' : ''} × 2 days).`);
    }
    
    return instructions.length > 0 ? instructions.join('\n') : '';
  };
  
  const cookingStyleInstructions = formatCookingStyleInstructions();

  // Runtime minimum quantities table for Section 6 — per meal type, accounting for batch cooking
  const servingsMinTable = ['breakfast', 'lunch', 'dinner'].map(mt => {
    const s = numberOfPeople * (effectiveCookingStyle[mt]?.cookFor2Days ? 2 : 1);
    const label = mt.charAt(0).toUpperCase() + mt.slice(1);
    if (mt === 'breakfast') {
      // Breakfasts can be light/composed and don't always have a
      // protein-centerpiece. Apply protein/carb minimums ONLY when the dish IS protein-centric;
      // otherwise allow realistic small portions.
      const proteinExamples = excludesEggs
        ? 'breakfast fish/meat/bean plate'
        : 'eggs scramble, omelet, breakfast meat/fish';
      const lightFormats = excludesGluten
        ? 'fruit bowl, rice cakes, corn tortillas'
        : 'fruit bowl, toast, sandwich';
      const pancakeFormat = excludesGluten || excludesEggs || excludesDairy ? '' : ', pancakes';
      const lightQuantities = excludesGluten
        ? `fruit ${150 * s}-${250 * s}g, rice cakes/corn tortillas ${80 * s}-${120 * s}g, allowed spread/topping ${30 * s}-${60 * s}g`
        : (excludesDairy
          ? `fruit ${150 * s}-${250 * s}g, bread ${80 * s}-${120 * s}g, allowed spread/topping ${30 * s}-${60 * s}g`
          : `yogurt ${150 * s}-${250 * s}g, cheese ${50 * s}-${80 * s}g, bread ${80 * s}-${120 * s}g`);
      return `   • ${label} (${s} serving${s > 1 ? 's' : ''}): IF the dish is protein-centric (${proteinExamples}): boneless protein ≥${150 * s}g | carb side ≥${150 * s}g. ELSE for light/composed breakfasts (${lightFormats}${pancakeFormat}): use realistic portions — e.g. ${lightQuantities}.`;
    }
    const vegPart = ` | vegetable side ≥${100 * s}g`;
    return `   • ${label} (${s} serving${s > 1 ? 's' : ''}): boneless protein ≥${150 * s}g | whole fish ≥${300 * s}g | bone-in meat ≥${250 * s}g | carb side ≥${150 * s}g${vegPart}`;
  }).join('\n');

  // Optimized compact prompt - conflict-free version

  // Get current month for seasonality
  const currentMonth = getCurrentMonthName();
  
  // Get supermarket name for the country
  const supermarketName = countryData.supermarket || (countryData.supermarkets && countryData.supermarkets[0]) || 'regular supermarkets';
  
  // Build restriction-specific swap examples based on detected restriction keys
  const getRestrictionSwapExamples = (restrictionKeys) => {
    const swapMappings = {
      'glutenFree': 'regular bread/pasta/flour/couscous → certified gluten-free equivalents; oats → certified gluten-free oats only',
      'lactoseFree': 'dairy → lactose-free milk/cheese; butter → olive oil',
      'dairyFree': 'dairy-dependent dishes → olive oil, tahini, blended beans, vegetable puree, or silken tofu if soy is allowed; rich sauces → broth or vegetable puree',
      'noEggs': 'eggs → chickpea flour omelet/tofu scramble; egg wash → oil brush',
      'noNuts': 'nuts → toasted seeds (sunflower/pumpkin); nut butter → seed butter',
      'noMushrooms': 'mushrooms → zucchini/eggplant/peppers',
      'noShellfish': 'shellfish → white fish/chicken',
      'noSoy': 'soy sauce → coconut aminos; tofu → chickpeas/lentils',
      'halal': 'pork → chicken/beef/fish/legumes; wine/sherry/beer/mirin/sake → broth, lemon juice, vinegar, herbs, or spices',
      'vegetarian': 'meat/fish → legumes/tofu/tempeh; chicken stock → vegetable stock',
      'vegan': 'all animal products → plant-based alternatives; honey → maple syrup'
    }; 
    
    const applicableSwaps = restrictionKeys
      .filter(key => swapMappings[key])
      .map(key => swapMappings[key]);
    
    return applicableSwaps.length > 0 ? applicableSwaps.join('; ') : 'forbidden items → allowed alternatives';
  };

  const getRestrictionHardRules = (restrictionKeys) => {
    const rules = [];

    if (restrictionKeys.includes('vegetarian')) {
      rules.push('   - VEGETARIAN means NO meat, poultry, fish, seafood, shellfish, gelatin, meat stock, chicken stock, fish stock, or animal-derived broth anywhere in title, description, or ingredients. Use legumes, tofu, tempeh, beans, lentils, chickpeas, grains, vegetables, dairy only if not otherwise forbidden, and vegetable stock.');
    }

    if (restrictionKeys.includes('glutenFree')) {
      rules.push('   - GLUTEN-FREE means NO regular bread, toast, pasta, couscous, wheat, flour, barley, rye, breadcrumbs, pancakes, waffles, or crepes. Bread/toast/pasta/oats/flour are allowed ONLY when explicitly labeled certified gluten-free or gluten-free in the ingredient name.');
    }

    if (restrictionKeys.includes('noEggs')) {
      rules.push('   - NO EGGS means no eggs, omelets, frittatas, quiche, mayonnaise, aioli, custard, meringue, pancakes, waffles, French toast, or egg binder/wash.');
    }

    return rules.join('\n');
  };

  // Build dietary swap instruction only when there are actual restrictions (stronger version with specific examples)
  const dietarySwapInstruction = formattedDietaryPreferences.hasRestrictions 
    ? `- CRITICAL: Inspirations may include restricted items. Before generating meals, sanitize inspirations: replace any forbidden ingredient with an allowed ${supermarketName} alternative while keeping the dish type. Swap examples: ${getRestrictionSwapExamples(formattedDietaryPreferences.restrictionKeys)}. Ensure forbidden items do not appear in title/description/ingredients. Only discard an inspiration if no reasonable swap exists.`
    : '';

  // Build FORBIDDEN INGREDIENTS section for explicit LLM constraint
  const forbiddenIngredientsText = getForbiddenIngredientsText(formattedDietaryPreferences.restrictionKeys, 30);
  const restrictionLabels = formattedDietaryPreferences.restrictionLabels || [];
  const forbiddenIngredientsSection = forbiddenIngredientsText
    ? `\n0) FORBIDDEN INGREDIENTS (HIGHEST PRIORITY — STRICT BAN)
   ⚠️ THE USER IS: ${restrictionLabels.join(', ').toUpperCase()}. This is a strict dietary restriction — zero tolerance.
   The following ingredients are STRICTLY FORBIDDEN. Do NOT use ANY of them in recipes (title, description, or ingredients):
   ${forbiddenIngredientsText}
${getRestrictionHardRules(formattedDietaryPreferences.restrictionKeys)}
   If an inspiration includes any forbidden item, REPLACE it with an allowed alternative or discard the inspiration entirely.\n`
    : '';

  // Build cuisine kit instructions when a specific cuisine is selected
  const buildCuisineKitInstructions = () => {
    const { cuisineAnchors, cuisineAvoid, flavorAnchors } = budgetExplanation;
    
    if (!cuisineAnchors && !flavorAnchors?.length && !cuisineAvoid?.length) {
      return '';
    }
    
    const parts = [];
    
    // Add flavor anchor requirement
    if (flavorAnchors?.length > 0) {
      parts.push(`- CUISINE ANCHORS (CRITICAL): Each meal MUST include at least 1-2 of these flavor anchors: ${flavorAnchors.slice(0, 6).join(', ')}.`);
    }
    
    // Add avoid list for single-cuisine selection
    if (cuisineAvoid?.length > 0) {
      parts.push(`- CUISINE AVOID: Do NOT use these ingredients (wrong cuisine profile): ${cuisineAvoid.join(', ')}.`);
    }
    
    // Add note about budget tier being soft, cuisine being hard
    if (flavorAnchors?.length > 0 || cuisineAvoid?.length > 0) {
      parts.push(`- PRECEDENCE: Cuisine selection is a HARD constraint. Budget-tier ingredient suggestions are SOFT and must only be used if they fit the selected cuisine.`);
    }
    
    return parts.length > 0 ? '\n   ' + parts.join('\n   ') : '';
  };
  
  const cuisineKitInstructions = buildCuisineKitInstructions();

  // Build explicit budget bounds for clarity
  const budgetBoundsText = budgetMin !== null && budgetMax !== null
    ? `Lower bound: ${budgetMin}, Upper bound: ${budgetMax} (${currencySymbol}, inclusive).`
    : budgetMax === null && budgetMin !== null
      ? `Minimum: ${budgetMin} ${currencySymbol}.`
      : '';

  // Build budget target text - gated based on whether upper bound exists
  const budgetTargetText = (budgetMax !== null && Number.isFinite(budgetMax))
    ? `- Target: approximately 90% of upper bound, but never exceed upper limit and never go below lower limit.`
    : `- Target: stay above the minimum and keep totalCost reasonable; there is no upper bound, but avoid unnecessarily expensive choices.`;

  // Check if any batch yields are enabled (for cost scope clarification)
  const hasBatchYields = effectiveCookingStyle.breakfast.cookFor2Days || 
                         effectiveCookingStyle.lunch.cookFor2Days || 
                         effectiveCookingStyle.dinner.cookFor2Days;
  
  // Build batch yield cost clarification
  const batchCostClarification = hasBatchYields
    ? `- totalCost must reflect groceries purchased to cover the full 9-meal plan including any batch yields.`
    : '';

  // Build complexity section text - gated based on whether advanced complexity is used
  // Complexity controls cooking METHOD only, never ingredient cost/quality (that's budget's job)
  const complexitySection = hasAdvancedComplexity
    ? `8) Complexity (cooking method only — does NOT affect ingredient cost/quality)
   - IMPORTANT: Complexity controls cooking TECHNIQUE and NUMBER OF STEPS, not ingredient selection or price. Use the same budget-tier ingredients regardless of complexity.
   - If a mealType complexity is "advanced" (see Section 2): multi-step recipes, homemade sauces/dressings, braising, marinating, and 2-3 components are encouraged.
   - If a mealType complexity is "simple": minimal steps, one-pan/one-pot, quick assembly — no elaborate techniques.
   - If a mealType complexity is "standard": common home cooking techniques with moderate steps.
   - All complexity levels: use only standard home kitchen equipment (oven, stovetop, basic pots/pans, blender). No special equipment (sous vide, pressure cooker, deep fryer, etc.).
   - For tight time limits, use pre-cut/frozen vegetables, quick-cook grains, or canned legumes.`
    : `8) Complexity (cooking method only — does NOT affect ingredient cost/quality)
   - Straightforward recipes only; keep steps minimal and techniques simple.
   - Use only standard home kitchen equipment. No special equipment (sous vide, pressure cooker, deep fryer, etc.).
   - For tight time limits, use pre-cut/frozen vegetables, quick-cook grains, or canned legumes.`;

  let prompt = `
Output valid JSON only. Do not include markdown formatting, comments, or extra text.

Return JSON with keys: "reasoning", "Number of people", "mealPlan", "totalCost".
- "reasoning": string — MUST start with EXACTLY this sentence: "My plan has 3 Breakfasts, 3 Lunches, 3 Dinners (9 total). I will not deviate from this distribution." Then briefly note: protein variety plan and any dietary restrictions. Keep under 80 words total. Do NOT propose a different mealType distribution under any circumstance.
- "Number of people": ${numberOfPeople}
- "mealPlan": array of exactly 9 items (3 Breakfast, 3 Lunch, 3 Dinner)
- "totalCost": "${currencySymbol}XX.XX" (estimated groceries total)

Fixed slot contract for mealPlan array (MUST follow exactly):
- mealPlan.length MUST be exactly 9. Valid array indexes are 0 through 8 only. Never create index 9 or a 10th object.
- Indexes 0, 1, 2 MUST be Breakfast.
- Indexes 3, 4, 5 MUST be Lunch.
- Indexes 6, 7, 8 MUST be Dinner.
- After the 3rd Lunch (index 5), the next item MUST be Dinner. Never output a 4th Breakfast, 4th Lunch, or 4th Dinner.
- After the 3rd Dinner (index 8), stop the mealPlan array immediately. Close the array and the JSON object; do not append another Dinner.
- Do not solve this dynamically; fill these fixed slots only.

Per-meal schema (all fields required):
- mealType: "Breakfast" | "Lunch" | "Dinner"
- title: string, <=60 chars, attractive, descriptive. MUST NOT include: countries, cuisines, cities, places, or chef names, NOR their demonym/adjective forms (e.g. NO "Spanish", "Greek", "Italian", "French", "Mediterranean", "Asian"). MUST NOT pair the dish with a beverage (no "with coffee", "with café con leche", "with tea", "with juice"). Use flavor/ingredient descriptors instead.${titleNativeException}
- description: string, <=200 chars, enticing summary of flavors, key ingredients, and cooking method. MUST NOT include: countries, cuisines, cities, places, chef names, or their demonym/adjective forms (no "Spanish", "Greek", "Italian", etc.).
- ingredients: array of { "product": string (MUST be in English), "qty": string, "productHint": string (MUST be in English, supermarket-ready noun phrase naming the EXACT product to buy), "form": one of "fresh" | "frozen" | "canned" | "jarred" | "dried" | "cured" | "prepared" | "dairy" | "other" }
  See the productHint rule in Section 3 below — both productHint and form are REQUIRED for every ingredient.
- cookingTime: string, number only (e.g. "25"), realistic total minutes including prep — must respect time limits in Section 2
- maxStorageTime: string, number only (e.g. "5"), how many days ALL ingredients can last when stored FRESH (prefer fridge/pantry over freezer). Usually 3-5 days. A value of 2 is allowed only for the single most-perishable first meal within a mealType and must satisfy Section 7.
  ORDERING HINT: within each meal type (3 meals), order from MOST PERISHABLE first to LEAST PERISHABLE last. The 1st meal is cooked first, the 3rd meal last — so the 3rd meal's ingredients need to last longer. For the 3rd meal in each type, prefer ingredients with naturally long fridge life (${filterByRestrictions(['eggs', 'cheese', 'canned goods', 'pantry staples', 'hardy vegetables like carrots/potatoes/cabbage'], restrictionKeys).join(', ')}).
  Reference shelf lives: ${filterByRestrictions([
    'milk/cream=5-7 (fridge)', 'eggs=21+ (fridge)', 'butter=30+ (fridge)', 'cheese=14+ (fridge)',
    'beef/pork whole cuts=3-5 (fridge)', 'chicken breast=2 (fridge)', 'minced meat=1-2 (fridge)',
    'fresh fish/seafood=1-2 (fridge)', 'bread=3-4 (fridge)', 'oats/rice/pasta/flour=30+ (pantry)',
    'honey/oil/vinegar=365+ (pantry)', 'fresh vegetables=5-7 (fridge)', 'fresh herbs=3-5 (fridge)',
    'yogurt=7-10 (fridge)', 'canned/jarred=30+ (pantry)'
  ], restrictionKeys).join(', ')}.

MEALPLAN STRUCTURE (HARD): Output mealPlan with exactly 9 items in this exact order:
1. Breakfast (cook first — can use most perishable ingredients)
2. Breakfast
3. Breakfast (cook last — prefer longer-shelf-life ingredients)
4. Lunch (cook first — can use most perishable ingredients)
5. Lunch
6. Lunch (cook last — prefer longer-shelf-life ingredients)
7. Dinner (cook first — can use most perishable ingredients)
8. Dinner
9. Dinner (cook last — prefer longer-shelf-life ingredients)
Each item’s mealType MUST match its position. Never output more or fewer than 9 items.
Within each meal type, order meals from most perishable to least perishable ingredients.

Global constraints (priority order):
${forbiddenIngredientsSection}
1) Budget (CRITICAL)
   - totalCost must be within ${cleanDisplayBudget}. ${budgetBoundsText}
   ${budgetTargetText}
   ${batchCostClarification}
   - ${budgetExplanation.prompt}
   - Ingredient selection: ${budgetExplanation.ingredientHints}
   - Portion sizing: ${budgetExplanation.portions}

2) Time limits & cooking complexity (does NOT affect ingredient selection or cost)
  - cookingTime is the TOTAL time start-to-plate (active hands-on + unattended passive). HARD OUTPUT LIMIT: every cookingTime MUST be <= the mealType TOTAL limit below. These are not suggestions.
  - Breakfast: total cookingTime MUST be <= ${breakfastTimeLimit} min (NO passive bonus — breakfast must stay quick: no baking, proving, setting, or passive waiting).
  - Lunch: total cookingTime MUST be <= ${lunchTimeLimitTotal} min; keep ACTIVE hands-on work to about ${lunchTimeLimit} min. The extra ${lunchPassiveBonus} min is ONLY for unattended oven/roast/bake/braise/simmer time — never active stovetop juggling.
  - Dinner: total cookingTime MUST be <= ${dinnerTimeLimitTotal} min; keep ACTIVE hands-on work to about ${dinnerTimeLimit} min. The extra ${dinnerPassiveBonus} min is ONLY for unattended oven/roast/bake/braise/simmer time — never active stovetop juggling.
  - PASSIVE TIME RULE (this is what makes a dish "easy"): keep total time as SHORT as the honest method allows — do NOT pad to the limit. Spend the passive allowance only when a dish genuinely needs unattended oven/simmer time (e.g. a casserole/bake with raw potatoes or root veg needs 30-45 min in the oven). A quick stovetop, salad, bowl or assembly dish must stay near its active time and not balloon. Strongly prefer ONE mostly-passive method (sheet-pan, oven-bake, one-pot) over pre-frying or pre-boiling components and THEN baking — that doubles the active work.
  - If a draft dish honestly needs more ACTIVE hands-on work than the active time above, redesign it before output: simplify the method, use supermarket-ready/pre-cooked components, quick-cook grains, one-pan assembly, or choose another concept.
  - Complexity controls cooking METHOD (number of steps, technique difficulty) — NOT ingredient quality or price. Use the same budget-tier ingredients regardless of complexity.${noOvernightRecipeRule}${quickBreakfastHardRule}${complexityInstructions ? '\n' + complexityInstructions : ''}

3) Ingredient naming, units and reuse (CRITICAL)
   - Language: ALL ingredient names MUST be in English, regardless of country or supermarket. Never use Spanish, French, or any other language for ingredient names.
   - Consistency: identical spelling for the same product across all meals (no synonyms).
   - Seasonality: only use ingredients commonly stocked in ${supermarketName} in ${effectiveCountry} in ${currentMonth}.
   - Reuse: share ingredients across meals to reduce cost/waste; use realistic package sizes.
${getProductHintRule()}
${getFullNamingExamplesBlock(restrictionKeys)}
   - Quantities: use ${countryData.measurementSystem} units (${isMetric ? 'g, ml, tbsp, tsp, unit' : 'oz, lb, fl oz, tbsp, tsp, unit'}). Prefer counts for produce ("1 onion").
  - ${getSupermarketReadyRule(restrictionKeys)}
  - ${getIngredientCompletenessRule(restrictionKeys)}
  - ${getNoBeveragesRule(restrictionKeys)}
  - ${getProportionalityRuleFull(restrictionKeys)}
  - ${getCookingComplexityRuleFull(restrictionKeys)}

4) Cuisine guidance
   - Target distribution: ${mealSettings.cuisineDistribution}.
   - Cuisine influences flavor/style but MUST NOT appear in meal titles. Format and style should be inferable from title and description.${cuisineKitInstructions}${cuisineHardConstraint.hardConstraintBlock}${multiCuisineConstraint.multiCuisineBlock}
   ${formattedDietaryPreferences.text}
   ${dietarySwapInstruction}

5) Variety
   Meal formats:
   - Each of the 3 lunches must use a DIFFERENT format (no repeated formats within lunch).
   - Each of the 3 dinners must use a DIFFERENT format (no repeated formats within dinner).
   - Each of the 3 breakfasts must use a DIFFERENT format (no repeated formats within breakfast).
   - Format must be clear from the meal title and description.
${formatExamplesLines}
   - Do not fallback to simple toast with tomato recipe for multiple meals; use the variety of breakfast inspirations provided to create distinct breakfast meals.
   
   Cooking methods:
   - Vary methods across the 3 lunch and 3 dinner meals; avoid using the same method more than twice.
   - Method examples: ${varietyPrompts.cookingPlan.methods.join(', ')}.
   
   Breakfast variety:
   - All 3 breakfasts must be different types.
   - Breakfast inspirations${cuisineHardConstraint.inspirationsAreHard ? ' (MANDATORY - you MUST use these exact templates)' : ''}: ${safeInspirations.breakfastInspirations.join(', ')}.
   
   ${cuisineHardConstraint.inspirationsAreHard ? 'Inspirations (MANDATORY - ALL 9 meals MUST be based on these templates, do not invent new formats)' : 'Inspirations (use as flavor/structure guidance)'}:
   - Lunch inspirations: ${safeInspirations.lunchInspirations.join(', ')}.
   - Dinner inspirations: ${safeInspirations.dinnerInspirations.join(', ')}.
   - Dinner structure: prefer main + side + vegetable.
   - EASY & HANDS-OFF: keep every chosen inspiration easy — one mostly-passive method (sheet-pan, oven-bake, one-pot, or quick assembly). If an inspiration would need deep-frying, breading-and-frying, hand-forming dumplings/croquettes, or constant stovetop attention, adapt it to a hands-off version of the same dish (e.g. oven-bake instead of deep-fry) while keeping its flavour identity.

6) Servings (CRITICAL)
   - Calculate all ingredient quantities per recipe batch as specified below.
   - MINIMUM QUANTITIES per recipe (MUST NOT go below these totals):
${servingsMinTable}
${cookingStyleInstructions}

7) Freshness distribution (CRITICAL)
  - ${getFreshnessDistributionRule(restrictionKeys)}

${complexitySection}

9) Self-check before output
   Before outputting JSON, mentally walk through EACH of the 9 meals one by one and verify: 
  (a) CRITICAL STRUCTURE: mealPlan.length is exactly 9, last index is 8, with mealType counts Breakfast=3, Lunch=3, Dinner=3. Indexes 0-2 are Breakfast, 3-5 are Lunch, 6-8 are Dinner. If there is a 10th object, a 4th Breakfast/Lunch/Dinner, or any wrong-position mealType, you MUST delete/rewrite before output. This is non-negotiable.
   (b) FORBIDDEN CHECK: scan EVERY ingredient in all 9 meals against the forbidden list.${forbiddenIngredientsText ? ` The user is ${restrictionLabels.join(', ').toUpperCase()} — if ANY of these appear in ingredients, title, or description, REPLACE the ingredient: ${forbiddenIngredientsText}.` : ''} If any match is found, revise that meal before output.
   (c) totalCost within budget bounds. If any check fails, revise internally and output only the corrected JSON,
   (d) verify no near-duplicate archetypes; if found, rewrite the duplicated meal(s),
   (e) every meal has cookingTime (number string) and maxStorageTime (number string). Typical maxStorageTime range is 2-5; values of 6-7 are allowed only for truly long-lasting composite dishes (dry pasta + canned sauce, pantry-staple bowls). Within each meal type, verify meals are ordered from most perishable ingredients (1st) to least perishable (3rd). If not, reorder.
   (f) PRODUCT HINT CHECK: every ingredient in every meal MUST have a non-empty "productHint" (English, supermarket-ready noun phrase, HEAD-NOUN-FIRST per Section 3 rule) AND a "form" value from the allowed enum (${PRODUCT_FORM_VALUES.join(' | ')}). Verify the hint STARTS with the actual food item (salmon, onion, mushroom, tuna...) followed by qualifiers/processing/packaging — NOT the other way around. Examples of correct order: "tuna canned light in olive oil", "mushrooms champignon fresh sliced", "salmon fillet fresh atlantic", "salt sea fine box". Examples of WRONG order to fix: "canned light tuna...", "fresh sliced champignon mushrooms", "fresh atlantic salmon fillet", "fine sea salt box". Also verify the hint matches the cooking use — tomato SAUCE must hint a canned/jarred tomato form, tuna SALAD with cold fish must hint a canned tuna form, raw seared tuna must hint "tuna steak fresh". The "form" field MUST be consistent with the productHint (canned hint -> "canned", fresh hint -> "fresh", etc.). If a hint or form is missing, mis-ordered, or inconsistent, fix it before output. THIS CHECK IS NON-NEGOTIABLE.
  ${hardCookingTimeSelfCheck}
   ${getIngredientQualitySelfCheck(numberOfPeople, effectiveCookingStyle, restrictionKeys)}
`;

  if (process.env.NODE_ENV !== 'production') {
    console.debug('Prompt length (chars):', prompt.length);
  }

  return prompt.trim();
};